"use client";

import {
  Activity,
  Archive,
  ArrowRight,
  BookOpen,
  BrainCircuit,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  ClipboardCheck,
  Compass,
  Copy,
  Database,
  Download,
  ExternalLink,
  FileText,
  FlaskConical,
  FolderOpen,
  Gauge,
  GitBranch,
  GraduationCap,
  Info,
  LayoutTemplate,
  Link2,
  LoaderCircle,
  LockKeyhole,
  Menu,
  MessageSquareText,
  Paperclip,
  RefreshCw,
  Save,
  Search,
  Share2,
  Settings2,
  ShieldCheck,
  Sparkles,
  Target,
  Trash2,
  Type,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildAiMessages, parseJsonResponse } from "./lib/ai-prompts";
import {
  createFictionalDemoState,
  createInitialState,
} from "./lib/defaults";
import {
  appendAiLog,
  clearScholarFlowLocalData,
  downloadText,
  listAiLogs,
  loadActiveProject,
  loadDirectoryHandle,
  saveDirectoryHandle,
  saveProject,
} from "./lib/local-store";
import {
  buildCompressionPayload,
  buildMasterPrompt,
  estimatePromptTokens,
  estimateProjectTokens,
} from "./lib/prompt-builder";
import { extractReferencePapers, type ExtractedPaper } from "./lib/paper-extract";
import { providerLabel, PROVIDER_PRESETS } from "./lib/providers";
import { applyRequirementPatch } from "./lib/requirement-intake";
import {
  buildShareBundle,
  parseShareBundle,
  sanitizeProjectForShare,
} from "./lib/share";
import { SKILL_CATALOG } from "./lib/skill-catalog";
import type {
  AiConfig,
  AiLog,
  AiResult,
  AiTask,
  ContextCapsule,
  DirectionCandidate,
  PaperPlan,
  ProjectState,
  PromptMode,
  RequirementIntakeResult,
  ResearchProfile,
  StageId,
  VenueBrief,
  VenueSource,
  WorkspaceReport,
} from "./lib/types";
import { PROMPT_MODES, STAGES } from "./lib/types";
import {
  ensureHandlePermission,
  persistAiLogToWorkspace,
  persistCapsuleToWorkspace,
  persistPromptToWorkspace,
  requestWorkspaceDirectory,
  scanWorkspace,
  scaffoldWorkspace,
  syncAuditRecordsToWorkspace,
} from "./lib/workspace";

function isStaticPagesHost() {
  return (
    typeof window !== "undefined" &&
    window.location.hostname.toLowerCase().endsWith(".github.io")
  );
}

type Notice = { tone: "success" | "error" | "info"; message: string };
type UiScale = "compact" | "standard" | "large";

const createDefaultAiConfig = (): AiConfig => ({
  mode: isStaticPagesHost() ? "direct" : "proxy",
  provider: "deepseek",
  baseUrl: "https://api.deepseek.com",
  model: "deepseek-chat",
  apiKey: "",
  persistKey: false,
  jsonMode: true,
});

const contributionOptions = [
  ["new_problem", "新问题"],
  ["new_mechanism", "新机制"],
  ["new_evidence", "新证据"],
  ["systems", "系统实现"],
  ["efficiency", "效率/成本"],
  ["benchmark", "Benchmark"],
];

const evidenceOptions = [
  ["main", "主实验"],
  ["ablation", "消融"],
  ["robustness", "鲁棒性"],
  ["hyperparameter", "超参"],
  ["case", "案例"],
  ["efficiency", "效率"],
  ["human", "人工评估"],
  ["deployment", "部署探针"],
];

const stageIcons: Record<StageId, React.ReactNode> = {
  venue: <Compass size={18} />,
  profile: <GraduationCap size={18} />,
  direction: <Target size={18} />,
  experiment: <FlaskConical size={18} />,
  paper: <BookOpen size={18} />,
  prompt: <BrainCircuit size={18} />,
  workspace: <FolderOpen size={18} />,
};

const stageFlowMeta: Record<
  StageId,
  { input: string; output: string; gate: string; promptMode: PromptMode }
> = {
  venue: {
    input: "官网 / CFP / 初始设想",
    output: "已核验投稿边界",
    gate: "事实与未知项分离",
    promptMode: "landscape",
  },
  profile: {
    input: "经历、资源与偏好",
    output: "研究者能力画像",
    gate: "优势与禁区明确",
    promptMode: "landscape",
  },
  direction: {
    input: "文献缺口 × 个人长处",
    output: "冻结的问题与机制",
    gate: "转向条件已记录",
    promptMode: "landscape",
  },
  experiment: {
    input: "核心主张与公平协议",
    output: "可执行实验提示词",
    gate: "指标、基线与证据冻结",
    promptMode: "experiment",
  },
  paper: {
    input: "冻结结果与引用证据",
    output: "论文论证合同",
    gate: "每项贡献有证据",
    promptMode: "paper",
  },
  prompt: {
    input: "全部稳定字段",
    output: "Codex 阶段化指令",
    gate: "边界与目录不丢失",
    promptMode: "master",
  },
  workspace: {
    input: "Codex 本地产物",
    output: "证据链与纠错建议",
    gate: "结果可追溯可交付",
    promptMode: "master",
  },
};

function mergeState(
  base: ProjectState,
  restored: ProjectState,
): ProjectState {
  return {
    ...base,
    ...restored,
    profile: { ...base.profile, ...restored.profile },
    experiment: { ...base.experiment, ...restored.experiment },
    paper: {
      ...base.paper,
      ...restored.paper,
      figures: restored.paper?.figures?.length
        ? restored.paper.figures
        : base.paper.figures,
    },
    selectedSkills: restored.selectedSkills?.length
      ? restored.selectedSkills
      : base.selectedSkills,
    stageNotes: { ...base.stageNotes, ...restored.stageNotes },
    referencePapers: restored.referencePapers ?? base.referencePapers,
    memory: { ...base.memory, ...restored.memory },
    workspace: { ...base.workspace, ...restored.workspace },
  };
}

function stageIsComplete(stage: StageId, state: ProjectState) {
  switch (stage) {
    case "venue":
      return Boolean(state.venueBrief?.facts.length);
    case "profile":
      return Boolean(state.profile.domain && state.profile.strengths);
    case "direction":
      return Boolean(state.selectedDirectionId);
    case "experiment":
      return Boolean(
        state.experiment.hypothesis &&
          state.experiment.taskDefinition &&
          state.experiment.datasets &&
          state.experiment.evaluationProtocol &&
          state.experiment.primaryMetric &&
          state.experiment.baselines,
      );
    case "paper":
      return Boolean(
        state.paper.centralClaim &&
          state.paper.contributions &&
          state.paper.figures.length >= 2,
      );
    case "prompt":
      return Boolean(
        state.selectedDirectionId &&
          state.experiment.taskDefinition &&
          state.paper.centralClaim &&
          buildMasterPrompt(state).length > 2000,
      );
    case "workspace":
      return Boolean(state.workspace.report);
  }
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

export function ScholarFlowApp() {
  const [state, setState] = useState<ProjectState>(() => createInitialState());
  const [activeStage, setActiveStage] = useState<StageId>("venue");
  const [mobileNav, setMobileNav] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [promptPreviewOpen, setPromptPreviewOpen] = useState(false);
  const [requirementOpen, setRequirementOpen] = useState(false);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [paperIntakeOpen, setPaperIntakeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [requirementDraft, setRequirementDraft] = useState("");
  const [requirementOnlyBlank, setRequirementOnlyBlank] = useState(true);
  const [requirementResult, setRequirementResult] =
    useState<RequirementIntakeResult | null>(null);
  const [paperFiles, setPaperFiles] = useState<ExtractedPaper[]>([]);
  const [paperIntakeResult, setPaperIntakeResult] =
    useState<RequirementIntakeResult | null>(null);
  const [paperOnlyBlank, setPaperOnlyBlank] = useState(true);
  const [paperExtracting, setPaperExtracting] = useState(false);
  const [uiScale, setUiScale] = useState<UiScale>("standard");
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [busy, setBusy] = useState<string>("");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [logs, setLogs] = useState<AiLog[]>([]);
  const [directoryHandle, setDirectoryHandle] =
    useState<FileSystemDirectoryHandle | null>(null);
  const [proxyStatus, setProxyStatus] = useState({
    checked: false,
    configured: false,
    model: "deepseek-chat",
  });
  const [aiConfig, setAiConfig] = useState<AiConfig>(
    createDefaultAiConfig,
  );

  const updateState = useCallback(
    (updater: (current: ProjectState) => ProjectState) => {
      setState((current) => ({
        ...updater(current),
        updatedAt: new Date().toISOString(),
      }));
    },
    [],
  );

  useEffect(() => {
    const load = async () => {
      const restored = await loadActiveProject();
      const base = createInitialState();
      const project = restored ? mergeState(base, restored) : base;
      setState(project);
      setLogs(await listAiLogs(project.id));
      const savedHandle = await loadDirectoryHandle(project.id);
      if (savedHandle) setDirectoryHandle(savedHandle);
      const storedConfig = localStorage.getItem("scholarflow.aiConfig");
      const persistedKey =
        localStorage.getItem("scholarflow.apiKey.persisted") || "";
      const sessionKey =
        sessionStorage.getItem("scholarflow.apiKey") || persistedKey;
      const storedScale =
        (localStorage.getItem("scholarflow.uiScale") as UiScale | null) ||
        "standard";
      setUiScale(storedScale);
      document.documentElement.dataset.uiScale = storedScale;
      setOnboardingDismissed(
        localStorage.getItem("scholarflow.onboardingDismissed") === "true",
      );
      if (storedConfig) {
        try {
          const parsed = JSON.parse(storedConfig) as Partial<AiConfig>;
          setAiConfig({
            ...createDefaultAiConfig(),
            ...parsed,
            mode: isStaticPagesHost()
              ? "direct"
              : parsed.mode || createDefaultAiConfig().mode,
            apiKey: sessionKey,
            persistKey: Boolean(persistedKey),
          });
        } catch {
          // Invalid local preferences fall back to defaults.
        }
      } else if (sessionKey) {
        setAiConfig((current) => ({
          ...current,
          mode: isStaticPagesHost() ? "direct" : current.mode,
          apiKey: sessionKey,
          persistKey: Boolean(persistedKey),
        }));
      } else if (isStaticPagesHost()) {
        setAiConfig((current) => ({ ...current, mode: "direct" }));
      }
      setHydrated(true);
    };
    void load();
  }, []);

  useEffect(() => {
    if (isStaticPagesHost()) {
      return;
    }
    fetch("/api/ai", { cache: "no-store" })
      .then((response) => response.json())
      .then((data: unknown) => {
        const status = data as { configured?: boolean; model?: string };
        setProxyStatus({
          checked: true,
          configured: Boolean(status.configured),
          model: status.model || "deepseek-chat",
        });
      })
      .catch(() =>
        setProxyStatus({
          checked: true,
          configured: false,
          model: "deepseek-chat",
        }),
      );
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const timer = window.setTimeout(() => {
      void saveProject(state);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [hydrated, state]);

  useEffect(() => {
    document.documentElement.dataset.uiScale = uiScale;
    if (hydrated) localStorage.setItem("scholarflow.uiScale", uiScale);
  }, [hydrated, uiScale]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 4200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const completedStages = STAGES.filter((stage) =>
    stageIsComplete(stage.id, state),
  ).length;
  const progress = Math.round((completedStages / STAGES.length) * 100);
  const tokenEstimate = estimateProjectTokens(state);
  const selectedDirection = state.directions.find(
    (item) => item.id === state.selectedDirectionId,
  );
  const deterministicPrompt = useMemo(
    () => buildMasterPrompt(state, state.promptMode),
    [state],
  );
  const activePrompt =
    state.refinedPrompts[state.promptMode] || deterministicPrompt;

  const callModel = useCallback(
    async (task: AiTask, payload: unknown): Promise<AiResult> => {
      if (aiConfig.mode === "proxy") {
        if (!aiConfig.apiKey && !proxyStatus.configured) {
          setSettingsOpen(true);
          throw new Error("请先配置模型连接或填写 API key。");
        }
        const response = await fetch("/api/ai", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(aiConfig.apiKey
              ? { "x-scholarflow-api-key": aiConfig.apiKey }
              : {}),
          },
          body: JSON.stringify({
            task,
            payload,
            model: aiConfig.model,
            jsonMode: aiConfig.jsonMode,
          }),
        });
        const body = (await response.json()) as AiResult & { error?: string };
        if (!response.ok) throw new Error(body.error || "AI request failed.");
        return body as AiResult;
      }

      if (!aiConfig.apiKey && aiConfig.provider !== "ollama") {
        setSettingsOpen(true);
        throw new Error("浏览器直连需要本次会话的 API key。");
      }
      const endpoint = `${aiConfig.baseUrl.replace(/\/$/, "")}/chat/completions`;
      const isDeepSeek =
        aiConfig.provider === "deepseek" || endpoint.includes("deepseek.com");
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          ...(aiConfig.apiKey
            ? { Authorization: `Bearer ${aiConfig.apiKey}` }
            : {}),
          "Content-Type": "application/json",
          ...(aiConfig.provider === "openrouter"
            ? {
                "HTTP-Referer": window.location.origin,
                "X-OpenRouter-Title": "ScholarFlow",
              }
            : {}),
        },
        body: JSON.stringify({
          model: aiConfig.model,
          messages: buildAiMessages(task, payload),
          ...(aiConfig.jsonMode
            ? { response_format: { type: "json_object" } }
            : {}),
          ...(isDeepSeek ? { thinking: { type: "disabled" } } : {}),
          temperature: 0.2,
          max_tokens: 7000,
        }),
      });
      const body = (await response.json()) as {
        error?: { message?: string };
        choices?: Array<{ message?: { content?: string } }>;
        model?: string;
        usage?: AiLog["usage"];
      };
      if (!response.ok) {
        throw new Error(body.error?.message || `HTTP ${response.status}`);
      }
      const content = body.choices?.[0]?.message?.content;
      if (!content) throw new Error("模型返回了空响应。");
      return {
        data: parseJsonResponse(content),
        model: body.model || aiConfig.model,
        usage: body.usage,
      };
    },
    [aiConfig, proxyStatus.configured],
  );

  const runAi = useCallback(
    async <T,>(task: AiTask, payload: unknown): Promise<T | undefined> => {
      const startedAt = new Date().toISOString();
      const id = crypto.randomUUID();
      setBusy(task);
      try {
        const { runResearchGraph } = await import("./lib/research-graph");
        const graphResult = await runResearchGraph(
          {
            task,
            payload,
            contextBudget: state.memory.contextBudget,
            threadId: `${state.id}:${task}`,
          },
          { callModel },
        );
        const completedAt = new Date().toISOString();
        const log: AiLog = {
          id,
          projectId: state.id,
          task,
          model: graphResult.result.model,
          mode: aiConfig.mode,
          startedAt,
          completedAt,
          request: payload,
          response: {
            data: graphResult.result.data,
            graphTrace: graphResult.trace,
            contextBound: graphResult.compressed,
          },
          usage: graphResult.result.usage,
        };
        await appendAiLog(log);
        setLogs((current) => [...current, log]);
        if (
          directoryHandle &&
          (await ensureHandlePermission(directoryHandle))
        ) {
          await persistAiLogToWorkspace(directoryHandle, log);
        }
        setNotice({
          tone: "success",
          message: graphResult.compressed
            ? "已完成，并在调用前自动收窄活动上下文。"
            : "模型已返回结构化结果，记录已进入本地审计日志。",
        });
        return graphResult.result.data as T;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "AI 调用失败。";
        const log: AiLog = {
          id,
          projectId: state.id,
          task,
          model: aiConfig.model,
          mode: aiConfig.mode,
          startedAt,
          completedAt: new Date().toISOString(),
          request: payload,
          response: null,
          error: message,
        };
        await appendAiLog(log);
        setLogs((current) => [...current, log]);
        setNotice({ tone: "error", message });
        return undefined;
      } finally {
        setBusy("");
      }
    },
    [
      aiConfig.mode,
      aiConfig.model,
      callModel,
      directoryHandle,
      state.id,
      state.memory.contextBudget,
    ],
  );

  const openPromptPreview = (mode?: PromptMode) => {
    if (mode) {
      updateState((current) => ({ ...current, promptMode: mode }));
    }
    setPromptPreviewOpen(true);
  };

  const analyzeRequirementDraft = async () => {
    if (requirementDraft.trim().length < 8) {
      setNotice({
        tone: "error",
        message: "请先写下至少一句具体需求。",
      });
      return;
    }
    const result = await runAi<RequirementIntakeResult>(
      "requirement_intake",
      {
        requirement: requirementDraft,
        currentProject: {
          projectName: state.projectName,
          idea: state.idea,
          venueUrls: state.venueUrls,
          profile: state.profile,
          experiment: state.experiment,
          paper: state.paper,
        },
        mergePolicy: requirementOnlyBlank
          ? "只建议填入当前空白字段；已有值保留"
          : "允许建议更新已有字段，但仍需逐项预览",
      },
    );
    if (result) {
      setRequirementResult({
        summary: result.summary || "已解析新增要求",
        detectedStages: Array.isArray(result.detectedStages)
          ? result.detectedStages
          : [],
        confidence: result.confidence || "low",
        warnings: Array.isArray(result.warnings) ? result.warnings : [],
        patch: result.patch || {},
        mappings: Array.isArray(result.mappings) ? result.mappings : [],
      });
    }
  };

  const applyParsedRequirements = () => {
    if (!requirementResult) return;
    const merged = applyRequirementPatch(
      state,
      requirementResult.patch,
      requirementOnlyBlank,
    );
    if (!merged.applied.length) {
      setNotice({
        tone: "info",
        message: merged.skipped.length
          ? "解析到的字段已有内容；可关闭“只填空白字段”后重新应用。"
          : "没有可安全写入的字段，请补充更具体的需求。",
      });
      return;
    }
    updateState(() => merged.state);
    const firstStage = requirementResult.detectedStages.find((stage) =>
      STAGES.some((item) => item.id === stage),
    );
    if (firstStage) setActiveStage(firstStage);
    setRequirementOpen(false);
    setRequirementDraft("");
    setRequirementResult(null);
    setNotice({
      tone: "success",
      message: `已写入 ${merged.applied.length} 个字段；${merged.skipped.length} 个已有字段保持不变。`,
    });
  };

  const extractPaperFiles = async (files: File[]) => {
    setPaperExtracting(true);
    setPaperIntakeResult(null);
    try {
      const extracted = await extractReferencePapers(files);
      setPaperFiles(extracted);
      setNotice({
        tone: "success",
        message: `已在浏览器中抽取 ${extracted.length} 篇论文；确认后才会调用模型。`,
      });
    } catch (error) {
      setPaperFiles([]);
      setNotice({
        tone: "error",
        message: error instanceof Error ? error.message : "论文解析失败。",
      });
    } finally {
      setPaperExtracting(false);
    }
  };

  const analyzeReferencePapers = async () => {
    if (!paperFiles.length) {
      setNotice({ tone: "error", message: "请先上传 1–5 篇参考论文。" });
      return;
    }
    const result = await runAi<RequirementIntakeResult>("paper_intake", {
      papers: paperFiles.map((paper) => ({
        filename: paper.name,
        pageCount: paper.pageCount,
        extractedText: paper.text,
      })),
      currentProject: {
        idea: state.idea,
        profile: state.profile,
        selectedDirection,
        experiment: state.experiment,
        paper: state.paper,
      },
      mergePolicy: paperOnlyBlank
        ? "只建议填入当前空白字段；已有值保留"
        : "允许建议更新已有字段，但仍需逐项预览",
    });
    if (result) {
      setPaperIntakeResult({
        summary: result.summary || "已从参考论文提取研究线索",
        detectedStages: Array.isArray(result.detectedStages)
          ? result.detectedStages
          : ["direction", "experiment", "paper"],
        confidence: result.confidence || "low",
        warnings: Array.isArray(result.warnings) ? result.warnings : [],
        patch: result.patch || {},
        mappings: Array.isArray(result.mappings) ? result.mappings : [],
      });
    }
  };

  const applyPaperIntake = () => {
    if (!paperIntakeResult) return;
    const merged = applyRequirementPatch(
      state,
      paperIntakeResult.patch,
      paperOnlyBlank,
    );
    const paperMetadata = paperFiles.map((paper) => ({
      id: paper.id,
      name: paper.name,
      size: paper.size,
      pageCount: paper.pageCount,
      charCount: paper.charCount,
      extractedAt: paper.extractedAt,
    }));
    const existingIds = new Set(state.referencePapers.map((paper) => paper.id));
    const newPapers = paperMetadata.filter((paper) => !existingIds.has(paper.id));
    updateState(() => ({
      ...merged.state,
      referencePapers: [...merged.state.referencePapers, ...newPapers],
    }));
    setPaperIntakeOpen(false);
    setPaperFiles([]);
    setPaperIntakeResult(null);
    setActiveStage("direction");
    setNotice({
      tone: "success",
      message: `已登记 ${newPapers.length} 篇论文并写入 ${merged.applied.length} 个字段；所有内容仍可编辑。`,
    });
  };

  const exportProjectBundle = () => {
    const safeState = sanitizeProjectForShare(state);
    const safePrompt = buildMasterPrompt(safeState, state.promptMode);
    const bundle = buildShareBundle(safeState, safePrompt);
    downloadText(
      `scholarflow-${state.projectName || "project"}.json`,
      JSON.stringify(bundle, null, 2),
      "application/json;charset=utf-8",
    );
    setNotice({
      tone: "success",
      message: "项目包已导出；API key、目录路径、日志和上下文胶囊未包含。",
    });
  };

  const importSharedFile = async (file: File) => {
    const text = await file.text();
    if (file.name.toLowerCase().endsWith(".json")) {
      try {
        const bundle = parseShareBundle(text);
        const imported = mergeState(createInitialState(), bundle.project);
        imported.id = crypto.randomUUID();
        imported.createdAt = new Date().toISOString();
        imported.updatedAt = imported.createdAt;
        imported.workspace = { absolutePath: "", handleName: "" };
        setState(imported);
        setLogs([]);
        setDirectoryHandle(null);
        setActiveStage("venue");
        setShareOpen(false);
        setNotice({
          tone: "success",
          message: "已作为新项目导入；连接配置和本地目录未被改变。",
        });
      } catch (error) {
        setNotice({
          tone: "error",
          message: error instanceof Error ? error.message : "项目包解析失败。",
        });
      }
      return;
    }
    if (text.trim().length < 30) {
      setNotice({ tone: "error", message: "导入的提示词内容过短。" });
      return;
    }
    updateState((current) => ({
      ...current,
      promptMode: "master",
      refinedPrompts: { ...current.refinedPrompts, master: text },
      stageNotes: {
        ...current.stageNotes,
        prompt:
          "已直接导入一份外部提示词作为 master 版本。使用前需核对其中的事实、路径、权限与证据边界。",
      },
    }));
    setShareOpen(false);
    setActiveStage("prompt");
    setNotice({
      tone: "success",
      message: "外部提示词已导入为 master 版本，可继续编辑、分享或交给 Codex。",
    });
  };

  const clearAllTraces = async () => {
    if (
      !window.confirm(
        "这会删除本浏览器中的全部 ScholarFlow 表单、日志、目录句柄、密钥和偏好，且无法恢复。继续吗？",
      )
    ) {
      return;
    }
    await clearScholarFlowLocalData();
    const fresh = createInitialState();
    setState(fresh);
    setLogs([]);
    setDirectoryHandle(null);
    setAiConfig(createDefaultAiConfig());
    setUiScale("standard");
    setOnboardingDismissed(false);
    setSettingsOpen(false);
    setActiveStage("venue");
    setNotice({
      tone: "success",
      message:
        "本机 ScholarFlow 数据已清空。若需撤销站点目录权限，请在浏览器站点设置中操作。",
    });
  };

  const toggleSkill = (skillId: string) => {
    updateState((current) => ({
      ...current,
      selectedSkills: current.selectedSkills.includes(skillId)
        ? current.selectedSkills.filter((id) => id !== skillId)
        : [...current.selectedSkills, skillId],
    }));
  };

  const downloadSkillPack = () => {
    setNotice({
      tone: "success",
      message: "ScholarFlow Skills Pack 已开始下载，包含 3 个原创技能与外部技能清单。",
    });
  };

  const fetchVenueSources = async () => {
    const urls = state.venueUrls
      .split("\n")
      .map((url) => url.trim())
      .filter(Boolean);
    if (!urls.length) {
      setNotice({ tone: "error", message: "请先填写至少一个公开 URL。" });
      return;
    }
    if (isStaticPagesHost()) {
      setNotice({
        tone: "error",
        message:
          "GitHub Pages 为纯静态预览，无法代读第三方网页。请粘贴征稿文本，或在本地完整模式中一键读取。",
      });
      return;
    }
    setBusy("venue_fetch");
    try {
      const response = await fetch("/api/venue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls }),
      });
      const body = (await response.json()) as {
        error?: string;
        sources: VenueSource[];
      };
      if (!response.ok) throw new Error(body.error || "页面读取失败。");
      updateState((current) => ({
        ...current,
        venueSources: body.sources,
      }));
      setNotice({
        tone: "success",
        message: `已读取 ${body.sources.filter((item: { status: string }) => item.status === "ready").length} 个来源。`,
      });
    } catch (error) {
      setNotice({
        tone: "error",
        message: error instanceof Error ? error.message : "页面读取失败。",
      });
    } finally {
      setBusy("");
    }
  };

  const analyzeVenue = async () => {
    const result = await runAi<VenueBrief>("venue_analysis", {
      idea: state.idea,
      sources: state.venueSources.map((source) => ({
        url: source.url,
        title: source.title,
        text: source.text,
        status: source.status,
        error: source.error,
      })),
    });
    if (result) {
      updateState((current) => ({ ...current, venueBrief: result }));
    }
  };

  const addManualVenueSource = () => {
    const text = state.venueManualText.trim();
    if (!text) {
      setNotice({ tone: "error", message: "请先粘贴 CFP 或投稿要求正文。" });
      return;
    }
    updateState((current) => ({
      ...current,
      venueSources: [
        ...current.venueSources.filter((source) => source.url !== "manual://venue-text"),
        {
          id: crypto.randomUUID(),
          url: "manual://venue-text",
          title: "用户粘贴的 venue 要求",
          excerpt: text.slice(0, 900),
          text,
          fetchedAt: new Date().toISOString(),
          status: "ready",
        },
      ],
    }));
    setNotice({
      tone: "success",
      message: "文本已作为本地来源加入，适合 GitHub Pages 或无法抓取的页面。",
    });
  };

  const generateInterview = async () => {
    const result = await runAi<{
      questions: VenueBrief["questions"];
      profileSummary: string;
      missingDecisions: string[];
    }>("research_interview", {
      idea: state.idea,
      venueBrief: state.venueBrief,
      profile: state.profile,
      existingAnswers: state.interviewAnswers,
    });
    if (result) {
      updateState((current) => ({
        ...current,
        venueBrief: {
          ...(current.venueBrief || {
            venueName: "",
            track: "",
            summary: "",
            facts: [],
            unknowns: result.missingDecisions,
            warnings: [],
            questions: [],
          }),
          questions: result.questions,
        },
      }));
    }
  };

  const generateDirections = async () => {
    const result = await runAi<{
      candidates: DirectionCandidate[];
      recommendation: string;
      comparisonLogic: string;
    }>("direction_candidates", {
      idea: state.idea,
      venueBrief: state.venueBrief,
      profile: state.profile,
      interviewAnswers: state.interviewAnswers,
    });
    if (result) {
      updateState((current) => ({
        ...current,
        directions: result.candidates,
        selectedDirectionId: "",
      }));
    }
  };

  const reviewExperiment = async () => {
    const result = await runAi<NonNullable<ProjectState["experiment"]["review"]>>(
      "experiment_review",
      {
        idea: state.idea,
        direction: selectedDirection,
        experiment: state.experiment,
        venueBrief: state.venueBrief,
      },
    );
    if (result) {
      updateState((current) => ({
        ...current,
        experiment: { ...current.experiment, review: result },
      }));
    }
  };

  const generatePaperPlan = async () => {
    const result = await runAi<NonNullable<PaperPlan["generatedPlan"]>>(
      "paper_plan",
      {
        idea: state.idea,
        venueBrief: state.venueBrief,
        direction: selectedDirection,
        experiment: state.experiment,
        paperPreferences: state.paper,
      },
    );
    if (result) {
      updateState((current) => ({
        ...current,
        paper: { ...current.paper, generatedPlan: result },
      }));
    }
  };

  const compressContext = async () => {
    const result = await runAi<Omit<ContextCapsule, "id" | "stage" | "tokenEstimate" | "createdAt">>(
      "context_compression",
      buildCompressionPayload(
        state,
        logs.slice(-8).map((log) => ({
          task: log.task,
          response: log.response,
        })),
      ),
    );
    if (!result) return;
    const capsule: ContextCapsule = {
      ...result,
      id: crypto.randomUUID(),
      stage: activeStage,
      tokenEstimate: Math.max(
        1,
        Math.ceil(JSON.stringify(result).length / 3),
      ),
      createdAt: new Date().toISOString(),
    };
    updateState((current) => ({
      ...current,
      memory: {
        ...current.memory,
        capsules: [...current.memory.capsules, capsule],
      },
    }));
    if (
      directoryHandle &&
      (await ensureHandlePermission(directoryHandle))
    ) {
      await persistCapsuleToWorkspace(directoryHandle, capsule);
    }
  };

  const refinePrompt = async () => {
    const result = await runAi<{
      prompt: string;
      changes: string[];
      remainingUnknowns: string[];
    }>("prompt_refinement", {
      mode: state.promptMode,
      deterministicPrompt,
    });
    if (result) {
      updateState((current) => ({
        ...current,
        refinedPrompts: {
          ...current.refinedPrompts,
          [current.promptMode]: result.prompt,
        },
      }));
    }
  };

  const bindWorkspace = async () => {
    try {
      const handle = await requestWorkspaceDirectory();
      if (!(await ensureHandlePermission(handle))) {
        throw new Error("未获得目录读写权限。");
      }
      await saveDirectoryHandle(state.id, handle);
      setDirectoryHandle(handle);
      updateState((current) => ({
        ...current,
        workspace: { ...current.workspace, handleName: handle.name },
      }));
      setNotice({
        tone: "success",
        message: `已软绑定 ${handle.name}。绝对路径仍需手动粘贴一次，供 Codex 使用。`,
      });
    } catch (error) {
      if ((error as DOMException)?.name === "AbortError") return;
      setNotice({
        tone: "error",
        message: error instanceof Error ? error.message : "目录绑定失败。",
      });
    }
  };

  const runWorkspaceScan = async () => {
    if (!directoryHandle) {
      setNotice({ tone: "error", message: "请先绑定本地目录。" });
      return;
    }
    setBusy("workspace_scan");
    try {
      if (!(await ensureHandlePermission(directoryHandle))) {
        throw new Error("目录权限已失效，请重新授权。");
      }
      const report = await scanWorkspace(directoryHandle);
      updateState((current) => ({
        ...current,
        workspace: { ...current.workspace, report },
      }));
      setNotice({
        tone: "success",
        message: `扫描完成：${report.fileCount} 个文件，证据链完成度 ${report.score}%。`,
      });
    } catch (error) {
      setNotice({
        tone: "error",
        message: error instanceof Error ? error.message : "扫描失败。",
      });
    } finally {
      setBusy("");
    }
  };

  const createWorkspaceScaffold = async () => {
    if (!directoryHandle) {
      setNotice({ tone: "error", message: "请先绑定一个空目录或项目根目录。" });
      return;
    }
    if (
      !window.confirm(
        "将创建 ScholarFlow 标准目录和缺失的模板文件；已有同名文件不会覆盖。继续吗？",
      )
    ) {
      return;
    }
    setBusy("workspace_scaffold");
    try {
      const results = await scaffoldWorkspace(
        directoryHandle,
        state,
        buildMasterPrompt(state, "master"),
      );
      const created = results.filter((item) => item.result === "created").length;
      updateState((current) => ({
        ...current,
        workspace: {
          ...current.workspace,
          scaffoldedAt: new Date().toISOString(),
        },
      }));
      setNotice({
        tone: "success",
        message: `目录契约已建立，新增 ${created} 个模板文件；已有文件未覆盖。`,
      });
      await runWorkspaceScan();
    } catch (error) {
      setNotice({
        tone: "error",
        message: error instanceof Error ? error.message : "脚手架写入失败。",
      });
    } finally {
      setBusy("");
    }
  };

  const syncAudit = async () => {
    if (!directoryHandle) {
      setNotice({ tone: "error", message: "请先绑定本地目录。" });
      return;
    }
    setBusy("audit_sync");
    try {
      const result = await syncAuditRecordsToWorkspace(
        directoryHandle,
        logs,
        state.memory.capsules,
      );
      setNotice({
        tone: "success",
        message: `已补写 ${result.logCount} 条对话和 ${result.capsuleCount} 份上下文胶囊。`,
      });
    } catch (error) {
      setNotice({
        tone: "error",
        message: error instanceof Error ? error.message : "审计记录同步失败。",
      });
    } finally {
      setBusy("");
    }
  };

  const analyzeWorkspace = async () => {
    if (!state.workspace.report) {
      setNotice({ tone: "error", message: "请先扫描工作区。" });
      return;
    }
    const result = await runAi<
      NonNullable<WorkspaceReport["aiAnalysis"]>
    >("workspace_analysis", {
      project: {
        idea: state.idea,
        selectedDirection,
        centralClaim: state.paper.centralClaim,
      },
      manifest: {
        fileCount: state.workspace.report.fileCount,
        files: state.workspace.report.files.map((file) => ({
          path: file.path,
          size: file.size,
        })),
        checks: state.workspace.report.checks,
      },
      snippets: state.workspace.report.snippets,
    });
    if (result) {
      updateState((current) => ({
        ...current,
        workspace: {
          ...current.workspace,
          report: current.workspace.report
            ? { ...current.workspace.report, aiAnalysis: result }
            : current.workspace.report,
        },
      }));
    }
  };

  const savePromptToWorkspace = async () => {
    if (!directoryHandle) {
      setNotice({ tone: "error", message: "请先绑定本地目录。" });
      return;
    }
    await persistPromptToWorkspace(
      directoryHandle,
      state.promptMode,
      activePrompt,
    );
    setNotice({
      tone: "success",
      message: "提示词已写入 prompts/generated/，可直接交给 Codex。",
    });
  };

  const saveAiSettings = () => {
    sessionStorage.setItem("scholarflow.apiKey", aiConfig.apiKey);
    if (aiConfig.persistKey && aiConfig.apiKey) {
      localStorage.setItem(
        "scholarflow.apiKey.persisted",
        aiConfig.apiKey,
      );
    } else {
      localStorage.removeItem("scholarflow.apiKey.persisted");
    }
    localStorage.setItem(
      "scholarflow.aiConfig",
      JSON.stringify({
        mode: aiConfig.mode,
        provider: aiConfig.provider,
        baseUrl: aiConfig.baseUrl,
        model: aiConfig.model,
        jsonMode: aiConfig.jsonMode,
      }),
    );
    setSettingsOpen(false);
    setNotice({
      tone: "success",
      message: aiConfig.persistKey
        ? "连接已保存；API key 记住在本浏览器中，可随时一键清空。"
        : "连接已保存；API key 仅保留在当前浏览器会话。",
    });
  };

  const testAiConnection = async () => {
    const result = await runAi<RequirementIntakeResult>(
      "requirement_intake",
      {
        requirement: "测试连接：不写入项目，只返回空 patch 和连接成功摘要。",
        currentProject: {},
        mergePolicy: "只测试结构化 JSON 响应，不建议任何字段",
      },
    );
    if (result) {
      setNotice({
        tone: "success",
        message: `${providerLabel(aiConfig.provider)} · ${aiConfig.model || "当前模型"} 连接测试成功。`,
      });
    }
  };

  const loadFictionalCase = () => {
    if (
      state.idea &&
      !window.confirm("加载示例会替换当前表单内容，继续吗？")
    ) {
      return;
    }
    const sample = createFictionalDemoState();
    setState(sample);
    setLogs([]);
    setDirectoryHandle(null);
    setActiveStage("venue");
    setNotice({
      tone: "info",
      message: "已载入虚构案例；其中不存在真实 venue、投稿信息或实验结果。",
    });
  };

  const renderStage = () => {
    switch (activeStage) {
      case "venue":
        return (
          <VenueStage
            state={state}
            busy={busy}
            updateState={updateState}
            fetchSources={fetchVenueSources}
            analyzeVenue={analyzeVenue}
            addManualSource={addManualVenueSource}
            loadFictionalCase={loadFictionalCase}
          />
        );
      case "profile":
        return (
          <ProfileStage
            state={state}
            busy={busy}
            updateState={updateState}
            generateInterview={generateInterview}
          />
        );
      case "direction":
        return (
          <DirectionStage
            state={state}
            busy={busy}
            updateState={updateState}
            generateDirections={generateDirections}
          />
        );
      case "experiment":
        return (
          <ExperimentStage
            state={state}
            busy={busy}
            updateState={updateState}
            reviewExperiment={reviewExperiment}
            previewPrompt={() => openPromptPreview("experiment")}
          />
        );
      case "paper":
        return (
          <PaperStage
            state={state}
            busy={busy}
            updateState={updateState}
            generatePaperPlan={generatePaperPlan}
          />
        );
      case "prompt":
        return (
          <PromptStage
            state={state}
            busy={busy}
            logs={logs}
            tokenEstimate={tokenEstimate}
            prompt={activePrompt}
            deterministicPrompt={deterministicPrompt}
            updateState={updateState}
            refinePrompt={refinePrompt}
            compressContext={compressContext}
            saveToWorkspace={savePromptToWorkspace}
          />
        );
      case "workspace":
        return (
          <WorkspaceStage
            state={state}
            busy={busy}
            logs={logs}
            directoryHandle={directoryHandle}
            updateState={updateState}
            bindWorkspace={bindWorkspace}
            scanWorkspace={runWorkspaceScan}
            scaffoldWorkspace={createWorkspaceScaffold}
            syncAudit={syncAudit}
            analyzeWorkspace={analyzeWorkspace}
          />
        );
    }
  };

  const currentIndex = STAGES.findIndex((stage) => stage.id === activeStage);
  const nextStage = STAGES[currentIndex + 1];

  return (
    <main className="scholar-app">
      <aside className={classNames("app-sidebar", mobileNav && "is-open")}>
        <div className="brand">
          <div className="brand-mark" aria-hidden="true">
            <GraduationCap size={24} strokeWidth={1.8} />
          </div>
          <div>
            <strong>ScholarFlow</strong>
            <span>学术航线</span>
          </div>
          <button
            className="icon-button sidebar-close"
            onClick={() => setMobileNav(false)}
            aria-label="关闭导航"
          >
            <X size={19} />
          </button>
        </div>

        <div className="progress-block">
          <div className="progress-caption">
            <span>项目进度</span>
            <strong>{progress}%</strong>
          </div>
          <div className="progress-track" aria-label={`项目进度 ${progress}%`}>
            <span style={{ width: `${progress}%` }} />
          </div>
          <p>{completedStages} / {STAGES.length} 个阶段形成可检查产物</p>
        </div>

        <nav className="stage-nav" aria-label="研究流程">
          {STAGES.map((stage) => {
            const complete = stageIsComplete(stage.id, state);
            return (
              <button
                key={stage.id}
                className={classNames(
                  "stage-nav-item",
                  activeStage === stage.id && "active",
                )}
                onClick={() => {
                  setActiveStage(stage.id);
                  setMobileNav(false);
                }}
              >
                <span className="stage-icon">{stageIcons[stage.id]}</span>
                <span className="stage-copy">
                  <span>
                    {stage.number} · {stage.label}
                  </span>
                  <small>{stage.helper}</small>
                </span>
                {complete ? (
                  <CheckCircle2 size={17} className="complete-icon" />
                ) : (
                  <Circle size={13} className="pending-icon" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-principle">
          <ShieldCheck size={18} />
          <p>
            <strong>本地优先</strong>
            表单、日志与压缩记忆留在你的设备；API key 不写入日志。
          </p>
        </div>
      </aside>

      <section className="app-shell">
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="icon-button mobile-menu"
              onClick={() => setMobileNav(true)}
              aria-label="打开导航"
            >
              <Menu size={20} />
            </button>
            <div>
              <span className="eyebrow">ACTIVE PROJECT</span>
              <input
                className="project-title-input"
                value={state.projectName}
                onChange={(event) =>
                  updateState((current) => ({
                    ...current,
                    projectName: event.target.value,
                  }))
                }
                aria-label="项目名称"
              />
            </div>
          </div>
          <div className="topbar-actions">
            <button
              className="topbar-command"
              onClick={() => setQuickStartOpen(true)}
            >
              <Sparkles size={15} />
              <span>快速开始</span>
            </button>
            <button
              className="topbar-command"
              onClick={() => setSkillsOpen(true)}
            >
              <GraduationCap size={15} />
              <span>技能包 {state.selectedSkills.length}</span>
            </button>
            <button
              className="topbar-command"
              onClick={() => setShareOpen(true)}
            >
              <Share2 size={15} />
              <span>导入 / 分享</span>
            </button>
            <button
              className="topbar-command prompt-command"
              onClick={() =>
                openPromptPreview(stageFlowMeta[activeStage].promptMode)
              }
            >
              <FileText size={15} />
              <span>最新提示词</span>
            </button>
            <button
              className={classNames(
                "topbar-command",
                "connection-command",
                aiConfig.apiKey || proxyStatus.configured
                  ? "connected"
                  : "disconnected",
              )}
              onClick={() => setSettingsOpen(true)}
            >
              <Settings2 size={15} />
              <span>
                {aiConfig.apiKey || proxyStatus.configured
                  ? `${providerLabel(aiConfig.provider)} · ${aiConfig.model || "模型"}`
                  : "连接模型"}
              </span>
            </button>
            <span className="status-pill status-muted">
              <Archive size={14} />
              {logs.length} 条审计记录
            </span>
          </div>
        </header>

        <PipelineRibbon
          state={state}
          activeStage={activeStage}
          onStageChange={setActiveStage}
        />

        <div className="content-shell">
          <section className="stage-content">
            {!onboardingDismissed && completedStages === 0 && (
              <OnboardingPanel
                onVenue={() => setActiveStage("venue")}
                onRequirements={() => setRequirementOpen(true)}
                onPapers={() => setPaperIntakeOpen(true)}
                onImport={() => setShareOpen(true)}
                onWorkspace={bindWorkspace}
                onDismiss={() => {
                  setOnboardingDismissed(true);
                  localStorage.setItem(
                    "scholarflow.onboardingDismissed",
                    "true",
                  );
                }}
              />
            )}
            {renderStage()}
            <StageAppendix
              stage={activeStage}
              value={state.stageNotes[activeStage] || ""}
              onChange={(value) =>
                updateState((current) => ({
                  ...current,
                  stageNotes: {
                    ...current.stageNotes,
                    [activeStage]: value,
                  },
                }))
              }
            />
          </section>
          <aside className="context-rail">
            <div className="context-card memory-card">
              <div className="context-card-head">
                <span>活动上下文</span>
                <BrainCircuit size={17} />
              </div>
              <strong>
                {tokenEstimate.toLocaleString()}
                <small> / {state.memory.contextBudget.toLocaleString()} tokens</small>
              </strong>
              <div className="mini-progress">
                <span
                  style={{
                    width: `${Math.min(
                      100,
                      (tokenEstimate / state.memory.contextBudget) * 100,
                    )}%`,
                  }}
                />
              </div>
              <p>
                {state.memory.capsules.length} 份上下文胶囊。原始对话保留，
                后续阶段只携带稳定事实与决定。
              </p>
              <button className="text-button" onClick={compressContext}>
                {busy === "context_compression" ? (
                  <LoaderCircle size={15} className="spin" />
                ) : (
                  <GitBranch size={15} />
                )}
                压缩当前阶段
              </button>
            </div>

            <div className="context-card">
              <div className="context-card-head">
                <span>执行边界</span>
                <LockKeyhole size={17} />
              </div>
              <ul className="compact-list">
                <li>结果必须可追溯到原始记录</li>
                <li>推断与事实分开标注</li>
                <li>主结果与 post-hoc 分开</li>
                <li>引用只用可核验来源</li>
              </ul>
            </div>

            <div className="context-card workspace-mini">
              <div className="context-card-head">
                <span>本地工作区</span>
                <FolderOpen size={17} />
              </div>
              <strong>
                {directoryHandle
                  ? directoryHandle.name
                  : "尚未绑定"}
              </strong>
              <p>
                {state.workspace.report
                  ? `${state.workspace.report.fileCount} 个文件 · 证据链 ${state.workspace.report.score}%`
                  : "绑定后可扫描 Codex 产物并固定写入审计日志。"}
              </p>
              <button
                className="text-button"
                onClick={() => setActiveStage("workspace")}
              >
                前往工作区 <ArrowRight size={15} />
              </button>
            </div>
          </aside>
        </div>

        {nextStage && (
          <footer className="stage-footer">
            <span>
              当前：{STAGES[currentIndex].label}
              {stageIsComplete(activeStage, state) && (
                <em><Check size={13} /> 已形成阶段产物</em>
              )}
            </span>
            <button
              className="primary-button compact"
              onClick={() => setActiveStage(nextStage.id)}
            >
              下一步：{nextStage.label}
              <ChevronRight size={17} />
            </button>
          </footer>
        )}
      </section>

      {settingsOpen && (
        <SettingsModal
          config={aiConfig}
          proxyStatus={proxyStatus}
          setConfig={setAiConfig}
          uiScale={uiScale}
          setUiScale={setUiScale}
          busy={busy}
          onTest={testAiConnection}
          onClear={clearAllTraces}
          onSave={saveAiSettings}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {quickStartOpen && (
        <QuickStartModal
          directoryBound={Boolean(directoryHandle)}
          onVenue={() => {
            setQuickStartOpen(false);
            setActiveStage("venue");
          }}
          onRequirements={() => {
            setQuickStartOpen(false);
            setRequirementOpen(true);
          }}
          onPapers={() => {
            setQuickStartOpen(false);
            setPaperIntakeOpen(true);
          }}
          onImport={() => {
            setQuickStartOpen(false);
            setShareOpen(true);
          }}
          onWorkspace={() => {
            setQuickStartOpen(false);
            void bindWorkspace();
          }}
          onClose={() => setQuickStartOpen(false)}
        />
      )}

      {promptPreviewOpen && (
        <PromptPreviewModal
          state={state}
          prompt={activePrompt}
          updateState={updateState}
          onClose={() => setPromptPreviewOpen(false)}
        />
      )}

      {requirementOpen && (
        <RequirementIntakeModal
          draft={requirementDraft}
          onlyBlank={requirementOnlyBlank}
          result={requirementResult}
          busy={busy}
          setDraft={(value) => {
            setRequirementDraft(value);
            setRequirementResult(null);
          }}
          setOnlyBlank={setRequirementOnlyBlank}
          onAnalyze={analyzeRequirementDraft}
          onApply={applyParsedRequirements}
          onClose={() => {
            setRequirementOpen(false);
            setRequirementResult(null);
          }}
        />
      )}

      {paperIntakeOpen && (
        <PaperIntakeModal
          papers={paperFiles}
          result={paperIntakeResult}
          onlyBlank={paperOnlyBlank}
          busy={busy}
          extracting={paperExtracting}
          onFiles={(files) => void extractPaperFiles(files)}
          onAnalyze={analyzeReferencePapers}
          onApply={applyPaperIntake}
          setOnlyBlank={setPaperOnlyBlank}
          onClose={() => {
            setPaperIntakeOpen(false);
            setPaperIntakeResult(null);
          }}
        />
      )}

      {shareOpen && (
        <ShareModal
          state={state}
          prompt={activePrompt}
          onExportProject={exportProjectBundle}
          onImport={(file) => void importSharedFile(file)}
          onClose={() => setShareOpen(false)}
        />
      )}

      {skillsOpen && (
        <SkillsModal
          selected={state.selectedSkills}
          onToggle={toggleSkill}
          onDownload={downloadSkillPack}
          onClose={() => setSkillsOpen(false)}
        />
      )}

      {notice && (
        <div className={classNames("toast", `toast-${notice.tone}`)}>
          {notice.tone === "success" ? (
            <CheckCircle2 size={18} />
          ) : notice.tone === "error" ? (
            <Info size={18} />
          ) : (
            <Sparkles size={18} />
          )}
          {notice.message}
        </div>
      )}
    </main>
  );
}

function OnboardingPanel({
  onVenue,
  onRequirements,
  onPapers,
  onImport,
  onWorkspace,
  onDismiss,
}: {
  onVenue: () => void;
  onRequirements: () => void;
  onPapers: () => void;
  onImport: () => void;
  onWorkspace: () => void;
  onDismiss: () => void;
}) {
  return (
    <section className="onboarding-panel">
      <div className="onboarding-copy">
        <span className="eyebrow">START HERE · 约 3 分钟</span>
        <h2>你可以从手里已有的任何材料开始</h2>
        <p>
          七阶段是一条推荐航线，不是锁定流程。所有阶段都能直接进入、随时编辑；
          单阶段提示词只携带它真正需要的前置证据。
        </p>
      </div>
      <div className="onboarding-actions">
        <button onClick={onVenue}>
          <Link2 size={19} />
          <span><strong>我有 venue 链接</strong><small>先核验投稿要求</small></span>
        </button>
        <button onClick={onRequirements}>
          <MessageSquareText size={19} />
          <span><strong>我有想法或要求</strong><small>让 AI 拆入表单</small></span>
        </button>
        <button onClick={onPapers}>
          <Paperclip size={19} />
          <span><strong>我有 1–5 篇论文</strong><small>本地抽取，一键帮填</small></span>
        </button>
        <button onClick={onImport}>
          <Upload size={19} />
          <span><strong>导入别人的项目</strong><small>JSON 或提示词</small></span>
        </button>
        <button onClick={onWorkspace}>
          <FolderOpen size={19} />
          <span><strong>先绑定本地目录</strong><small>浏览器直接读写</small></span>
        </button>
      </div>
      <button className="onboarding-dismiss" onClick={onDismiss}>
        我熟悉流程，收起引导
      </button>
    </section>
  );
}

function StageAppendix({
  stage,
  value,
  onChange,
}: {
  stage: StageId;
  value: string;
  onChange: (value: string) => void;
}) {
  const stageLabel = STAGES.find((item) => item.id === stage)?.label || stage;
  return (
    <section className="stage-appendix">
      <div>
        <span className="eyebrow">FREEFORM APPENDIX</span>
        <h2>{stageLabel}阶段自由附录</h2>
        <p>
          表单未覆盖的偏好、特殊约束、导师意见或交付要求都可以写在这里，
          会进入该阶段提示词，且始终保持可编辑。
        </p>
      </div>
      <textarea
        rows={5}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={`补充${stageLabel}阶段的自由要求，例如特殊数据协议、禁用方法、图表格式或人工检查规则……`}
      />
    </section>
  );
}

function QuickStartModal({
  directoryBound,
  onVenue,
  onRequirements,
  onPapers,
  onImport,
  onWorkspace,
  onClose,
}: {
  directoryBound: boolean;
  onVenue: () => void;
  onRequirements: () => void;
  onPapers: () => void;
  onImport: () => void;
  onWorkspace: () => void;
  onClose: () => void;
}) {
  const directorySupported =
    typeof window !== "undefined" && "showDirectoryPicker" in window;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="settings-modal quick-start-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-start-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">AI QUICK START</span>
            <h2 id="quick-start-title">从你已有的材料开始</h2>
            <p>
              无需依次填完 1–5。选择最接近的入口，AI 只建议、先预览，
              你仍然可以修改每一个字段。
            </p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </div>
        <div className="quick-start-grid">
          <button onClick={onVenue}>
            <Link2 size={22} /><strong>会议 / 期刊链接</strong>
            <span>核验 CFP、赛道和投稿边界</span>
          </button>
          <button onClick={onRequirements}>
            <MessageSquareText size={22} /><strong>粗略想法 / 零散要求</strong>
            <span>解析后回填空白字段</span>
          </button>
          <button onClick={onPapers}>
            <Paperclip size={22} /><strong>上传 1–5 篇论文</strong>
            <span>提取问题、机制、数据和指标</span>
          </button>
          <button onClick={onImport}>
            <Share2 size={22} /><strong>导入项目 / 提示词</strong>
            <span>复用他人的 ScholarFlow 航线</span>
          </button>
          <button
            onClick={onWorkspace}
            disabled={!directorySupported}
          >
            <FolderOpen size={22} /><strong>绑定本地目录</strong>
            <span>
              {directoryBound
                ? "当前项目已绑定，可重新选择"
                : directorySupported
                  ? "无需本地部署，浏览器直接授权"
                  : "当前浏览器暂不支持目录读写"}
            </span>
          </button>
        </div>
        <div className="privacy-banner">
          <ShieldCheck size={19} />
          <p>
            GitHub Pages 版本即可保存表单、绑定目录和导入导出。只有点击 AI
            分析时，选定文本才会发送到你配置的模型供应商。
          </p>
        </div>
      </section>
    </div>
  );
}

function PaperIntakeModal({
  papers,
  result,
  onlyBlank,
  busy,
  extracting,
  onFiles,
  onAnalyze,
  onApply,
  setOnlyBlank,
  onClose,
}: {
  papers: ExtractedPaper[];
  result: RequirementIntakeResult | null;
  onlyBlank: boolean;
  busy: string;
  extracting: boolean;
  onFiles: (files: File[]) => void;
  onAnalyze: () => void;
  onApply: () => void;
  setOnlyBlank: (value: boolean) => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="settings-modal wide-modal paper-intake-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paper-intake-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">PAPER-TO-PLAN</span>
            <h2 id="paper-intake-title">上传 1–5 篇论文，一键形成研究起点</h2>
            <p>
              PDF 在浏览器本地抽取；模型负责识别问题、机制、数据、指标、基线和局限。
              扫描版 PDF 需要先 OCR。
            </p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </div>
        <label className="paper-upload-zone">
          <Upload size={24} />
          <strong>{extracting ? "正在本地解析…" : "选择 PDF、TXT 或 Markdown"}</strong>
          <span>最多 5 篇，单篇不超过 30MB；不会上传到 ScholarFlow 服务器</span>
          <input
            type="file"
            accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
            multiple
            disabled={extracting}
            onChange={(event) =>
              onFiles(Array.from(event.target.files || []).slice(0, 5))
            }
          />
        </label>
        {papers.length > 0 && (
          <div className="paper-file-list">
            {papers.map((paper) => (
              <article key={paper.id}>
                <FileText size={18} />
                <div>
                  <strong>{paper.name}</strong>
                  <span>
                    {paper.pageCount} 页 · 已抽取 {paper.charCount.toLocaleString()} 字符
                  </span>
                </div>
                <CheckCircle2 size={17} />
              </article>
            ))}
          </div>
        )}
        <label className="merge-policy">
          <input
            type="checkbox"
            checked={onlyBlank}
            onChange={(event) => setOnlyBlank(event.target.checked)}
          />
          <span>
            <strong>只填空白字段</strong>
            <small>已有研究决定不会被论文内容静默覆盖。</small>
          </span>
        </label>
        <div className="paper-intake-actions">
          <AiButton
            task="paper_intake"
            busy={busy}
            onClick={onAnalyze}
            disabled={!papers.length || extracting}
          >
            AI 一键分析并生成字段映射
          </AiButton>
          <span>调用前可先在“连接模型”中选择任意兼容供应商。</span>
        </div>
        {result && (
          <ParsePreview
            result={result}
            onlyBlank={onlyBlank}
            onApply={onApply}
            applyLabel="确认写入研究计划"
          />
        )}
      </section>
    </div>
  );
}

function ParsePreview({
  result,
  onlyBlank,
  onApply,
  applyLabel,
}: {
  result: RequirementIntakeResult;
  onlyBlank: boolean;
  onApply: () => void;
  applyLabel: string;
}) {
  return (
    <div className="requirement-result">
      <div className="requirement-summary">
        <div>
          <span className="eyebrow">PARSE PREVIEW</span>
          <strong>{result.summary}</strong>
        </div>
        <span className={`confidence-tag ${result.confidence}`}>
          {result.confidence} confidence
        </span>
      </div>
      <div className="detected-stages">
        {result.detectedStages.map((stage) => (
          <span key={stage}>
            {STAGES.find((item) => item.id === stage)?.label || stage}
          </span>
        ))}
      </div>
      <div className="mapping-list">
        {result.mappings.map((mapping, index) => (
          <article key={`${mapping.targetField}-${index}`}>
            <span className="mapping-source">“{mapping.sourceQuote}”</span>
            <ChevronRight size={14} />
            <div>
              <strong>{mapping.targetField}</strong>
              <p>{mapping.value}</p>
            </div>
          </article>
        ))}
      </div>
      {result.warnings.length > 0 && (
        <div className="parse-warnings">
          <Info size={16} />
          <ul>
            {result.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="modal-footer">
        <span className="status-line">
          <ShieldCheck size={13} />
          {onlyBlank ? "已有字段保持不变" : "允许更新已有字段"}
        </span>
        <button className="primary-button" onClick={onApply}>
          <Check size={16} /> {applyLabel}
        </button>
      </div>
    </div>
  );
}

function ShareModal({
  state,
  prompt,
  onExportProject,
  onImport,
  onClose,
}: {
  state: ProjectState;
  prompt: string;
  onExportProject: () => void;
  onImport: (file: File) => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="settings-modal share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">PORTABLE RESEARCH PLAN</span>
            <h2 id="share-title">导入、导出与分享</h2>
            <p>
              没有账号也能传播研究航线。项目包不包含 API key、AI 日志、
              本机目录路径或上下文胶囊。
            </p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </div>
        <div className="share-grid">
          <article>
            <Share2 size={22} />
            <h3>分享完整项目</h3>
            <p>导出结构化 JSON，别人导入后仍可编辑全部阶段。</p>
            <button className="primary-button" onClick={onExportProject}>
              <Download size={16} /> 导出项目包
            </button>
          </article>
          <article>
            <FileText size={22} />
            <h3>只分享当前提示词</h3>
            <p>适合直接交给 Codex，或作为普通 Markdown 传播。</p>
            <div className="button-row">
              <button
                className="secondary-button"
                onClick={() => void navigator.clipboard.writeText(prompt)}
              >
                <Copy size={16} /> 复制
              </button>
              <button
                className="secondary-button"
                onClick={() =>
                  downloadText(
                    `scholarflow-${state.promptMode}-prompt.md`,
                    prompt,
                  )
                }
              >
                <Download size={16} /> 下载
              </button>
            </div>
          </article>
        </div>
        <label className="share-import-zone">
          <Upload size={21} />
          <span>
            <strong>导入 ScholarFlow JSON 或外部 Markdown/TXT 提示词</strong>
            <small>JSON 作为新项目；普通提示词作为 master 版本导入。</small>
          </span>
          <input
            type="file"
            accept=".json,.md,.txt,application/json,text/plain,text/markdown"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onImport(file);
            }}
          />
        </label>
      </section>
    </div>
  );
}

function PipelineRibbon({
  state,
  activeStage,
  onStageChange,
}: {
  state: ProjectState;
  activeStage: StageId;
  onStageChange: (stage: StageId) => void;
}) {
  return (
    <div className="pipeline-ribbon" aria-label="七阶段研究流程">
      <div className="pipeline-scroll">
        {STAGES.map((stage, index) => {
          const complete = stageIsComplete(stage.id, state);
          const active = stage.id === activeStage;
          return (
            <div className="pipeline-unit" key={stage.id}>
              <button
                className={classNames(
                  "pipeline-node",
                  complete && "complete",
                  active && "active",
                )}
                onClick={() => onStageChange(stage.id)}
                aria-current={active ? "step" : undefined}
              >
                <span>{stage.number}</span>
                <strong>{stage.label}</strong>
                <small>{stageFlowMeta[stage.id].output}</small>
              </button>
              {index < STAGES.length - 1 && (
                <ChevronRight className="pipeline-arrow" size={14} />
              )}
            </div>
          );
        })}
      </div>
      <div className="pipeline-gate">
        <span>当前输入：{stageFlowMeta[activeStage].input}</span>
        <strong>通过条件：{stageFlowMeta[activeStage].gate}</strong>
      </div>
    </div>
  );
}

function StageIntro({
  eyebrow,
  title,
  description,
  icon,
}: {
  eyebrow: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}) {
  return (
    <header className="stage-intro">
      <div className="stage-intro-icon">{icon}</div>
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </header>
  );
}

function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={classNames("field", className)}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function AiButton({
  task,
  busy,
  onClick,
  children,
  disabled,
}: {
  task: string;
  busy: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      className="primary-button"
      onClick={onClick}
      disabled={disabled || Boolean(busy)}
    >
      {busy === task ? (
        <LoaderCircle size={17} className="spin" />
      ) : (
        <Sparkles size={17} />
      )}
      {children}
    </button>
  );
}

function VenueStage({
  state,
  busy,
  updateState,
  fetchSources,
  analyzeVenue,
  addManualSource,
  loadFictionalCase,
}: {
  state: ProjectState;
  busy: string;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  fetchSources: () => void;
  analyzeVenue: () => void;
  addManualSource: () => void;
  loadFictionalCase: () => void;
}) {
  const readySources = state.venueSources.filter(
    (source) => source.status === "ready",
  ).length;
  return (
    <>
      <StageIntro
        eyebrow="01 · START WITH EVIDENCE"
        title="从一条网址，找到真正值得做的问题"
        description="先读取会议或期刊的一手要求，再让已连接模型针对你的经历追问。这里形成的约束，会跟随整条科研链。"
        icon={<Compass size={25} />}
      />

      <div className="hero-panel">
        <div className="hero-copy">
          <span className="hero-kicker">不要从“我要写什么模型”开始</span>
          <h2>从 venue、你擅长的事，以及一个朦胧设想开始。</h2>
          <p>
            ScholarFlow 会把网页证据与研究画像分开保存，未知要求保持未知，
            不让模型用惯例补齐事实。
          </p>
        </div>
        <button className="secondary-button" onClick={loadFictionalCase}>
          <LayoutTemplate size={17} />
          加载虚构演示案例
        </button>
      </div>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="step-badge">A</span>
            <h2>给出入口</h2>
          </div>
          <span className="quiet-note">最多读取 4 个公开 HTML 页面</span>
        </div>
        <div className="form-grid">
          <Field label="会议 / 期刊 / 赛道网址" className="span-2">
            <textarea
              rows={4}
              placeholder={"https://conference.org/cfp\nhttps://conference.org/track"}
              value={state.venueUrls}
              onChange={(event) =>
                updateState((current) => ({
                  ...current,
                  venueUrls: event.target.value,
                }))
              }
            />
          </Field>
          <Field
            label="现在脑海里的设想"
            hint="可以很粗糙，例如：想让 agent 在工具变化后仍能可靠工作。"
            className="span-2"
          >
            <textarea
              rows={5}
              placeholder="写下问题、应用、技术兴趣，或你觉得行业里一直没解决的麻烦……"
              value={state.idea}
              onChange={(event) =>
                updateState((current) => ({
                  ...current,
                  idea: event.target.value,
                }))
              }
            />
          </Field>
          <Field
            label="可选：粘贴 CFP / 投稿要求正文"
            hint="GitHub Pages 没有抓取后端，或网页禁止读取时使用；内容只保存在本地项目。"
            className="span-2"
          >
            <textarea
              rows={5}
              placeholder="粘贴 call for papers、track requirements 或期刊 author guidelines……"
              value={state.venueManualText}
              onChange={(event) =>
                updateState((current) => ({
                  ...current,
                  venueManualText: event.target.value,
                }))
              }
            />
          </Field>
        </div>
        <div className="button-row">
          <button
            className="secondary-button"
            onClick={fetchSources}
            disabled={Boolean(busy)}
          >
            {busy === "venue_fetch" ? (
              <LoaderCircle size={17} className="spin" />
            ) : (
              <Search size={17} />
            )}
            读取来源页面
          </button>
          <button
            className="secondary-button"
            onClick={addManualSource}
            disabled={!state.venueManualText.trim()}
          >
            <ClipboardCheck size={17} />
            加入粘贴文本
          </button>
          <AiButton
            task="venue_analysis"
            busy={busy}
            onClick={analyzeVenue}
            disabled={!readySources}
          >
            梳理要求并开始访谈
          </AiButton>
        </div>
      </section>

      {state.venueSources.length > 0 && (
        <section className="panel">
          <div className="panel-heading">
            <div>
              <span className="step-badge">B</span>
              <h2>来源快照</h2>
            </div>
            <span className="quiet-note">{readySources} 个读取成功</span>
          </div>
          <div className="source-list">
            {state.venueSources.map((source) => (
              <article className="source-card" key={source.id}>
                <div
                  className={classNames(
                    "source-state",
                    source.status === "ready" ? "ready" : "error",
                  )}
                >
                  {source.status === "ready" ? (
                    <Check size={14} />
                  ) : (
                    <X size={14} />
                  )}
                </div>
                <div>
                  <h3>{source.title}</h3>
                  {source.url.startsWith("http") ? (
                    <a href={source.url} target="_blank" rel="noreferrer">
                      {source.url} <ExternalLink size={12} />
                    </a>
                  ) : (
                    <span className="local-source-label">本地粘贴来源</span>
                  )}
                  <p>
                    {source.status === "ready"
                      ? source.excerpt
                      : source.error}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {state.venueBrief && (
        <section className="panel venue-brief">
          <div className="panel-heading">
            <div>
              <span className="step-badge done">
                <Check size={14} />
              </span>
              <h2>{state.venueBrief.venueName || "Venue brief"}</h2>
            </div>
            <span className="topic-chip">
              {state.venueBrief.track || "track 待确认"}
            </span>
          </div>
          <p className="lead-copy">{state.venueBrief.summary}</p>
          <div className="fact-grid">
            {state.venueBrief.facts.map((fact, index) => (
              <article className="fact-card" key={`${fact.label}-${index}`}>
                <span>{fact.label}</span>
                <strong>{fact.value}</strong>
                <a href={fact.evidenceUrl} target="_blank" rel="noreferrer">
                  证据来源 <Link2 size={12} />
                </a>
              </article>
            ))}
          </div>
          {(state.venueBrief.unknowns.length > 0 ||
            state.venueBrief.warnings.length > 0) && (
            <div className="split-callouts">
              <div className="callout callout-amber">
                <Info size={18} />
                <div>
                  <strong>仍需确认</strong>
                  <ul>
                    {state.venueBrief.unknowns.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="callout">
                <ShieldCheck size={18} />
                <div>
                  <strong>边界提醒</strong>
                  <ul>
                    {state.venueBrief.warnings.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}

function ProfileStage({
  state,
  busy,
  updateState,
  generateInterview,
}: {
  state: ProjectState;
  busy: string;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  generateInterview: () => void;
}) {
  const setProfile = <K extends keyof ResearchProfile>(
    key: K,
    value: ResearchProfile[K],
  ) =>
    updateState((current) => ({
      ...current,
      profile: { ...current.profile, [key]: value },
    }));
  const questions = state.venueBrief?.questions ?? [];
  return (
    <>
      <StageIntro
        eyebrow="02 · RESEARCHER FIT"
        title="把你的长处，变成选题的先验"
        description="好的方向不是排行榜上最热的方向，而是问题重要、证据可得，并且你的能力结构能形成独特优势的交集。"
        icon={<GraduationCap size={25} />}
      />

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="step-badge">A</span>
            <h2>研究画像</h2>
          </div>
          <AiButton
            task="research_interview"
            busy={busy}
            onClick={generateInterview}
          >
            生成高信息增益追问
          </AiButton>
        </div>
        <div className="form-grid">
          <Field label="熟悉的研究领域">
            <input
              value={state.profile.domain}
              onChange={(event) => setProfile("domain", event.target.value)}
              placeholder="例如：LLM agents、推荐系统、强化学习"
            />
          </Field>
          <Field label="实践经验">
            <input
              value={state.profile.experience}
              onChange={(event) => setProfile("experience", event.target.value)}
              placeholder="做过哪些项目、论文或工程"
            />
          </Field>
          <Field label="真正擅长的事" className="span-2">
            <textarea
              rows={3}
              value={state.profile.strengths}
              onChange={(event) => setProfile("strengths", event.target.value)}
              placeholder="实现、理论、数据、系统、评测、领域知识……"
            />
          </Field>
          <Field label="可复用资产">
            <textarea
              rows={3}
              value={state.profile.priorAssets}
              onChange={(event) => setProfile("priorAssets", event.target.value)}
              placeholder="代码、数据、模型权限、团队经验"
            />
          </Field>
          <Field label="必须避开的方向或重叠">
            <textarea
              rows={3}
              value={state.profile.avoidTopics}
              onChange={(event) => setProfile("avoidTopics", event.target.value)}
              placeholder="审稿中工作、已听腻的套路、无法获取的数据"
            />
          </Field>
          <Field label="本地算力">
            <input
              value={state.profile.compute}
              onChange={(event) => setProfile("compute", event.target.value)}
              placeholder="GPU / CPU / 内存 / 可运行模型规模"
            />
          </Field>
          <Field label="模型与 API">
            <input
              value={state.profile.modelAccess}
              onChange={(event) => setProfile("modelAccess", event.target.value)}
              placeholder="DeepSeek、OpenAI、Ollama、本地模型"
            />
          </Field>
          <Field label="数据条件">
            <input
              value={state.profile.dataAccess}
              onChange={(event) => setProfile("dataAccess", event.target.value)}
              placeholder="公开数据、企业数据、标注能力"
            />
          </Field>
          <Field label="预算">
            <input
              value={state.profile.budget}
              onChange={(event) => setProfile("budget", event.target.value)}
              placeholder="调用成本、人工标注、云算力"
            />
          </Field>
          <Field label="截止日期">
            <input
              value={state.profile.deadline}
              onChange={(event) => setProfile("deadline", event.target.value)}
              placeholder="YYYY-MM-DD 或阶段里程碑"
            />
          </Field>
          <Field label="风险偏好">
            <select
              value={state.profile.riskTolerance}
              onChange={(event) =>
                setProfile(
                  "riskTolerance",
                  event.target.value as ResearchProfile["riskTolerance"],
                )
              }
            >
              <option value="conservative">保守：优先可完成与稳健证据</option>
              <option value="balanced">平衡：新颖性与可实现性兼顾</option>
              <option value="ambitious">进取：允许更高探索风险</option>
            </select>
          </Field>
          <Field label="偏好的贡献形态" className="span-2">
            <div className="chip-selector">
              {contributionOptions.map(([id, label]) => {
                const selected =
                  state.profile.preferredContributions.includes(id);
                return (
                  <button
                    type="button"
                    key={id}
                    className={classNames("choice-chip", selected && "selected")}
                    onClick={() =>
                      setProfile(
                        "preferredContributions",
                        selected
                          ? state.profile.preferredContributions.filter(
                              (item) => item !== id,
                            )
                          : [...state.profile.preferredContributions, id],
                      )
                    }
                  >
                    {selected && <Check size={13} />} {label}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      </section>

      {questions.length > 0 && (
        <section className="panel">
          <div className="panel-heading">
            <div>
              <span className="step-badge">B</span>
              <h2>自适应访谈</h2>
            </div>
            <span className="quiet-note">只问会改变选题的信息</span>
          </div>
          <div className="question-stack">
            {questions.map((question, index) => (
              <article className="question-card" key={question.id}>
                <span className="question-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3>{question.question}</h3>
                  <p>{question.why}</p>
                  {question.answerType === "choice" &&
                  question.options?.length ? (
                    <select
                      value={state.interviewAnswers[question.id] || ""}
                      onChange={(event) =>
                        updateState((current) => ({
                          ...current,
                          interviewAnswers: {
                            ...current.interviewAnswers,
                            [question.id]: event.target.value,
                          },
                        }))
                      }
                    >
                      <option value="">请选择</option>
                      {question.options.map((option) => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <textarea
                      rows={question.answerType === "long" ? 4 : 2}
                      value={state.interviewAnswers[question.id] || ""}
                      onChange={(event) =>
                        updateState((current) => ({
                          ...current,
                          interviewAnswers: {
                            ...current.interviewAnswers,
                            [question.id]: event.target.value,
                          },
                        }))
                      }
                      placeholder="你的回答会进入研究画像和后续提示词"
                    />
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function DirectionStage({
  state,
  busy,
  updateState,
  generateDirections,
}: {
  state: ProjectState;
  busy: string;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  generateDirections: () => void;
}) {
  return (
    <>
      <StageIntro
        eyebrow="03 · PROBLEM BEFORE METHOD"
        title="选择一个能成立的故事，而非最热的技术名词"
        description="候选方向同时回答：问题为什么重要、现有方法为什么不够、你的机制为什么可能有效，以及当前资源能否产生足够证据。"
        icon={<Target size={25} />}
      />
      <section className="panel direction-control">
        <div>
          <h2>候选方向工作台</h2>
          <p>
            模型负责比较和追问，不替你承诺 SOTA。选择后会冻结成
            decision record，并进入实验契约。
          </p>
        </div>
        <AiButton
          task="direction_candidates"
          busy={busy}
          onClick={generateDirections}
        >
          生成 3 个实质不同的方向
        </AiButton>
      </section>

      {state.directions.length ? (
        <div className="direction-grid">
          {state.directions.map((direction, index) => {
            const selected = state.selectedDirectionId === direction.id;
            return (
              <article
                className={classNames("direction-card", selected && "selected")}
                key={direction.id}
              >
                <div className="direction-card-top">
                  <span>方向 {String(index + 1).padStart(2, "0")}</span>
                  {selected && (
                    <span className="selected-mark">
                      <Check size={13} /> 已选择
                    </span>
                  )}
                </div>
                <h2>{direction.title}</h2>
                <p className="direction-one-line">{direction.oneLine}</p>
                <div className="score-row">
                  <Score label="新颖性" value={direction.novelty} />
                  <Score label="可实现" value={direction.feasibility} />
                  <Score label="证据" value={direction.evidenceReadiness} />
                </div>
                <dl className="direction-details">
                  <div>
                    <dt>研究问题</dt>
                    <dd>{direction.researchQuestion}</dd>
                  </div>
                  <div>
                    <dt>关键缺口</dt>
                    <dd>{direction.unresolvedGap}</dd>
                  </div>
                  <div>
                    <dt>独特机制</dt>
                    <dd>{direction.proposedMechanism}</dd>
                  </div>
                  <div>
                    <dt>评测入口</dt>
                    <dd>{direction.benchmarkPlan}</dd>
                  </div>
                </dl>
                <div className="risk-box">
                  <strong>主要风险</strong>
                  <ul>
                    {direction.risks.map((risk) => (
                      <li key={risk}>{risk}</li>
                    ))}
                  </ul>
                  <small>转向信号：{direction.pivotSignal}</small>
                </div>
                <button
                  className={classNames(
                    "select-direction",
                    selected && "selected",
                  )}
                  onClick={() =>
                    updateState((current) => ({
                      ...current,
                      selectedDirectionId: direction.id,
                    }))
                  }
                >
                  {selected ? <Check size={16} /> : <ArrowRight size={16} />}
                  {selected ? "已冻结为当前方向" : "选择这个方向"}
                </button>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<Compass size={26} />}
          title="还没有候选方向"
          description="先补齐研究画像，再让已连接模型用同一套证据比较三个方向。"
        />
      )}
    </>
  );
}

function Score({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}/5</strong>
    </div>
  );
}

function ExperimentStage({
  state,
  busy,
  updateState,
  reviewExperiment,
  previewPrompt,
}: {
  state: ProjectState;
  busy: string;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  reviewExperiment: () => void;
  previewPrompt: () => void;
}) {
  const setExperiment = (
    key: keyof ProjectState["experiment"],
    value: unknown,
  ) =>
    updateState((current) => ({
      ...current,
      experiment: { ...current.experiment, [key]: value },
    }));
  const review = state.experiment.review;
  return (
    <>
      <StageIntro
        eyebrow="04 · EVIDENCE CONTRACT"
        title="先规定什么算证据，再让 Codex 去跑实验"
        description="实验不是结果仓库，而是一组用来排除替代解释的工具。这里冻结比较口径、停止条件和证据边界。"
        icon={<FlaskConical size={25} />}
      />
      <section className="panel experiment-prompt-panel">
        <div className="panel-heading">
          <div>
            <span className="step-badge">A</span>
            <h2>实验执行提示词骨架</h2>
          </div>
          <button className="secondary-button" onClick={previewPrompt}>
            <FileText size={16} /> 预览当前实验提示词
          </button>
        </div>
        <div className="prompt-form-note">
          <BrainCircuit size={19} />
          <p>
            这里定义 Codex 实际要实现什么、用什么栈、运行多少组合，以及哪条假设
            可以被证伪。每次填写都会立即进入顶部“最新提示词”。
          </p>
        </div>
        <div className="form-grid">
          <Field label="可被证伪的核心假设" className="span-2">
            <textarea
              rows={3}
              value={state.experiment.hypothesis}
              onChange={(event) =>
                setExperiment("hypothesis", event.target.value)
              }
              placeholder="在同数据、同模型、同预算下，哪个机制应改善哪个主指标，同时不能损害什么边界指标？"
            />
          </Field>
          <Field label="任务定义" className="span-2">
            <textarea
              rows={3}
              value={state.experiment.taskDefinition}
              onChange={(event) =>
                setExperiment("taskDefinition", event.target.value)
              }
              placeholder="输入、允许信息、输出、成功条件与失败边界"
            />
          </Field>
          <Field label="实现栈与工程约束">
            <textarea
              rows={4}
              value={state.experiment.implementationStack}
              onChange={(event) =>
                setExperiment("implementationStack", event.target.value)
              }
              placeholder="语言、框架、模型/API、本地算力、依赖、接口与禁止事项"
            />
          </Field>
          <Field label="运行矩阵">
            <textarea
              rows={4}
              value={state.experiment.runMatrix}
              onChange={(event) =>
                setExperiment("runMatrix", event.target.value)
              }
              placeholder="数据 × 模型 × 基线 × seed × 场景；先写预试验，再冻结主运行"
            />
          </Field>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="step-badge">B</span>
            <h2>公平比较与评价协议</h2>
          </div>
          {state.selectedDirectionId ? (
            <span className="topic-chip">方向已冻结</span>
          ) : (
            <span className="warning-chip">尚未选择方向</span>
          )}
        </div>
        <div className="form-grid">
          <Field label="数据集与切分">
            <textarea
              rows={4}
              value={state.experiment.datasets}
              onChange={(event) =>
                setExperiment("datasets", event.target.value)
              }
              placeholder="公开数据、版本、train/dev/test、许可证"
            />
          </Field>
          <Field label="基线">
            <textarea
              rows={4}
              value={state.experiment.baselines}
              onChange={(event) =>
                setExperiment("baselines", event.target.value)
              }
              placeholder="至少覆盖无方法、强通用方法、最接近工作和合理工程基线"
            />
          </Field>
          <Field label="评测协议与预算对齐" className="span-2">
            <textarea
              rows={3}
              value={state.experiment.evaluationProtocol}
              onChange={(event) =>
                setExperiment("evaluationProtocol", event.target.value)
              }
              placeholder="版本、切分、模型、token/调用预算、开发集选择与测试集冻结规则"
            />
          </Field>
          <Field label="主指标">
            <input
              value={state.experiment.primaryMetric}
              onChange={(event) =>
                setExperiment("primaryMetric", event.target.value)
              }
              placeholder="只选最能承载核心主张的指标"
            />
          </Field>
          <Field label="次要指标">
            <input
              value={state.experiment.secondaryMetrics}
              onChange={(event) =>
                setExperiment("secondaryMetrics", event.target.value)
              }
              placeholder="效率、成本、安全、选择性等"
            />
          </Field>
          <Field label="主实验比较" className="span-2">
            <textarea
              rows={3}
              value={state.experiment.mainComparison}
              onChange={(event) =>
                setExperiment("mainComparison", event.target.value)
              }
              placeholder="哪张表回答哪个研究问题，公平比较需要满足什么条件"
            />
          </Field>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="step-badge">C</span>
            <h2>机制证据、压力测试与交付</h2>
          </div>
        </div>
        <div className="form-grid">
          <Field label="消融">
            <textarea
              rows={4}
              value={state.experiment.ablations}
              onChange={(event) =>
                setExperiment("ablations", event.target.value)
              }
              placeholder="每项消融对应一个机制假设，避免信息不匹配的伪消融"
            />
          </Field>
          <Field label="鲁棒性">
            <textarea
              rows={4}
              value={state.experiment.robustness}
              onChange={(event) =>
                setExperiment("robustness", event.target.value)
              }
              placeholder="分布变化、噪声、规模、模型变化、失败注入"
            />
          </Field>
          <Field label="超参数">
            <textarea
              rows={3}
              value={state.experiment.hyperparameters}
              onChange={(event) =>
                setExperiment("hyperparameters", event.target.value)
              }
              placeholder="少量关键参数、范围和选择规则"
            />
          </Field>
          <Field label="Case study">
            <textarea
              rows={3}
              value={state.experiment.caseStudy}
              onChange={(event) =>
                setExperiment("caseStudy", event.target.value)
              }
              placeholder="成功、失败和边界案例各解释什么"
            />
          </Field>
          <Field label="随机性控制">
            <textarea
              rows={3}
              value={state.experiment.seeds}
              onChange={(event) => setExperiment("seeds", event.target.value)}
            />
          </Field>
          <Field label="资源预算">
            <textarea
              rows={3}
              value={state.experiment.computeBudget}
              onChange={(event) =>
                setExperiment("computeBudget", event.target.value)
              }
              placeholder="GPU 小时、API 调用、人工检查、最大迭代轮次"
            />
          </Field>
          <Field label="Artifact 与复现文件" className="span-2">
            <textarea
              rows={3}
              value={state.experiment.artifactPlan}
              onChange={(event) =>
                setExperiment("artifactPlan", event.target.value)
              }
              placeholder="配置、manifest、原始逐样本记录、处理脚本、汇总表、环境锁与 README"
            />
          </Field>
          <Field label="主表与绘图计划" className="span-2">
            <textarea
              rows={3}
              value={state.experiment.reportingPlan}
              onChange={(event) =>
                setExperiment("reportingPlan", event.target.value)
              }
              placeholder="每张表/图回答哪个研究问题；绘图只读取冻结 processed 文件"
            />
          </Field>
          <Field label="停止条件">
            <textarea
              rows={3}
              value={state.experiment.stopCriteria}
              onChange={(event) =>
                setExperiment("stopCriteria", event.target.value)
              }
            />
          </Field>
          <Field label="转向条件">
            <textarea
              rows={3}
              value={state.experiment.pivotCriteria}
              onChange={(event) =>
                setExperiment("pivotCriteria", event.target.value)
              }
            />
          </Field>
          <Field label="需要形成的证据" className="span-2">
            <div className="chip-selector">
              {evidenceOptions.map(([id, label]) => {
                const selected =
                  state.experiment.requiredEvidence.includes(id);
                return (
                  <button
                    type="button"
                    className={classNames("choice-chip", selected && "selected")}
                    key={id}
                    onClick={() =>
                      setExperiment(
                        "requiredEvidence",
                        selected
                          ? state.experiment.requiredEvidence.filter(
                              (item) => item !== id,
                            )
                          : [...state.experiment.requiredEvidence, id],
                      )
                    }
                  >
                    {selected && <Check size={13} />} {label}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
        <div className="button-row">
          <AiButton
            task="experiment_review"
            busy={busy}
            onClick={reviewExperiment}
          >
            检查实验能否支持主张
          </AiButton>
        </div>
      </section>

      {review && (
        <section className="panel review-panel">
          <div className="review-score">
            <span>契约完整度</span>
            <strong>{review.score}</strong>
            <small>/ 100</small>
          </div>
          <div className="review-copy">
            <h2>{review.summary}</h2>
            <div className="review-columns">
              <div>
                <strong>优先补齐</strong>
                <ul>
                  {review.actions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong>风险信号</strong>
                <ul>
                  {review.redFlags.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function PaperStage({
  state,
  busy,
  updateState,
  generatePaperPlan,
}: {
  state: ProjectState;
  busy: string;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  generatePaperPlan: () => void;
}) {
  const setPaper = (key: keyof PaperPlan, value: unknown) =>
    updateState((current) => ({
      ...current,
      paper: { ...current.paper, [key]: value },
    }));
  return (
    <>
      <StageIntro
        eyebrow="05 · ARGUMENT, NOT DIARY"
        title="让论文围绕最强证据组织"
        description="论文只保留最终成立的逻辑：问题为何重要、现有方法为何不够、机制做了什么，以及证据如何排除替代解释。"
        icon={<BookOpen size={25} />}
      />
      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="step-badge">A</span>
            <h2>论证合同</h2>
          </div>
          <AiButton task="paper_plan" busy={busy} onClick={generatePaperPlan}>
            生成证据驱动的论文骨架
          </AiButton>
        </div>
        <div className="form-grid">
          <Field label="暂定标题" className="span-2">
            <input
              value={state.paper.workingTitle}
              onChange={(event) =>
                setPaper("workingTitle", event.target.value)
              }
              placeholder="问题 + 独特机制或能力；避免空泛口号"
            />
          </Field>
          <Field label="读者应该记住的核心主张" className="span-2">
            <textarea
              rows={3}
              value={state.paper.centralClaim}
              onChange={(event) =>
                setPaper("centralClaim", event.target.value)
              }
              placeholder="一句可以被主实验直接支持的话"
            />
          </Field>
          <Field label="贡献" className="span-2">
            <textarea
              rows={4}
              value={state.paper.contributions}
              onChange={(event) =>
                setPaper("contributions", event.target.value)
              }
              placeholder="每项贡献对应形式化、机制、数据或实验中的明确证据"
            />
          </Field>
          <Field label="叙事主线">
            <textarea
              rows={5}
              value={state.paper.narrative}
              onChange={(event) => setPaper("narrative", event.target.value)}
              placeholder="从问题到证据的因果链，不写研发日记"
            />
          </Field>
          <Field label="章节结构">
            <textarea
              rows={5}
              value={state.paper.sections}
              onChange={(event) => setPaper("sections", event.target.value)}
            />
          </Field>
          <Field label="引用规则">
            <textarea
              rows={4}
              value={state.paper.citationRules}
              onChange={(event) =>
                setPaper("citationRules", event.target.value)
              }
            />
          </Field>
          <Field label="写作约束">
            <textarea
              rows={4}
              value={state.paper.writingConstraints}
              onChange={(event) =>
                setPaper("writingConstraints", event.target.value)
              }
            />
          </Field>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="step-badge">B</span>
            <h2>SCI 绘图技能接口</h2>
          </div>
          <span className="skill-chip">
            <Sparkles size={14} /> {state.paper.figureSkill}
          </span>
        </div>
        <div className="callout skill-callout">
          <BrainCircuit size={20} />
          <div>
            <strong>ScholarFlow 不重造绘图器</strong>
            <p>
              最终提示词会要求 Codex 读取共享技能的 SKILL.md，并把 brief、
              草稿、editable source、PDF、PNG 和视觉检查记录写入固定目录。
            </p>
          </div>
        </div>
        <Field label="Codex 中的技能调用名">
          <input
            value={state.paper.figureSkill}
            onChange={(event) => setPaper("figureSkill", event.target.value)}
            placeholder="$sci-diagram-drawing"
          />
        </Field>
        <div className="figure-spec-grid">
          {state.paper.figures.map((figure, index) => (
            <article className="figure-spec" key={figure.id}>
              <div className="figure-number">FIG {index + 1}</div>
              <Field label="角色">
                <input
                  value={figure.role}
                  onChange={(event) =>
                    setPaper(
                      "figures",
                      state.paper.figures.map((item) =>
                        item.id === figure.id
                          ? { ...item, role: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="读者应获得的信息">
                <textarea
                  rows={3}
                  value={figure.message}
                  onChange={(event) =>
                    setPaper(
                      "figures",
                      state.paper.figures.map((item) =>
                        item.id === figure.id
                          ? { ...item, message: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="证据边界">
                <textarea
                  rows={3}
                  value={figure.evidence}
                  onChange={(event) =>
                    setPaper(
                      "figures",
                      state.paper.figures.map((item) =>
                        item.id === figure.id
                          ? { ...item, evidence: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
              </Field>
            </article>
          ))}
        </div>
      </section>

      {state.paper.generatedPlan && (
        <section className="panel generated-plan">
          <div className="panel-heading">
            <div>
              <span className="step-badge done"><Check size={14} /></span>
              <h2>模型生成的论证建议</h2>
            </div>
          </div>
          <p className="lead-copy">{state.paper.generatedPlan.story}</p>
          <div className="generated-grid">
            <div>
              <strong>贡献候选</strong>
              <ul>
                {state.paper.generatedPlan.contributions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <strong>潜在审稿问题</strong>
              <ul>
                {state.paper.generatedPlan.likelyReviewerQuestions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function PromptStage({
  state,
  busy,
  logs,
  tokenEstimate,
  prompt,
  deterministicPrompt,
  updateState,
  refinePrompt,
  compressContext,
  saveToWorkspace,
}: {
  state: ProjectState;
  busy: string;
  logs: AiLog[];
  tokenEstimate: number;
  prompt: string;
  deterministicPrompt: string;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  refinePrompt: () => void;
  compressContext: () => void;
  saveToWorkspace: () => void;
}) {
  const copied = useRef<number | null>(null);
  const [copyLabel, setCopyLabel] = useState("复制给 Codex");
  const copyPrompt = async () => {
    await navigator.clipboard.writeText(prompt);
    setCopyLabel("已复制");
    if (copied.current) window.clearTimeout(copied.current);
    copied.current = window.setTimeout(() => setCopyLabel("复制给 Codex"), 1800);
  };
  return (
    <>
      <StageIntro
        eyebrow="06 · HANDOFF TO CODEX"
        title="把复杂研究，压成一份不丢边界的执行指令"
        description="确定性模板负责不遗漏，已连接模型只做压缩与消歧。原始资料和完整交互保留在本地，Codex 接收的是稳定上下文。"
        icon={<BrainCircuit size={25} />}
      />
      <section className="panel">
        <div className="panel-heading prompt-heading">
          <div>
            <span className="step-badge">A</span>
            <h2>选择交付范围</h2>
          </div>
          <span className="quiet-note">
            当前约 {Math.ceil(prompt.length / 3).toLocaleString()} tokens
          </span>
        </div>
        <div className="prompt-mode-grid">
          {PROMPT_MODES.map((mode) => (
            <button
              key={mode.id}
              className={classNames(
                "prompt-mode",
                state.promptMode === mode.id && "selected",
              )}
              onClick={() =>
                updateState((current) => ({
                  ...current,
                  promptMode: mode.id,
                }))
              }
            >
              <div className="prompt-mode-title">
                <strong>{mode.label}</strong>
                <span>{mode.description}</span>
              </div>
              <dl className="prompt-mode-contract">
                <div>
                  <dt>只读取</dt>
                  <dd>{mode.context}</dd>
                </div>
                <div>
                  <dt>要产出</dt>
                  <dd>{mode.deliverable}</dd>
                </div>
                <div>
                  <dt>不介入</dt>
                  <dd>{mode.excludes}</dd>
                </div>
              </dl>
              {state.promptMode === mode.id && <CheckCircle2 size={17} />}
            </button>
          ))}
        </div>
      </section>

      <section className="prompt-editor">
        <div className="prompt-toolbar">
          <div>
            <FileText size={17} />
            <span>
              {state.refinedPrompts[state.promptMode]
                ? "模型润色版"
                : "确定性模板版"}
            </span>
          </div>
          <div className="toolbar-actions">
            {state.refinedPrompts[state.promptMode] && (
              <button
                onClick={() =>
                  updateState((current) => ({
                    ...current,
                    refinedPrompts: {
                      ...current.refinedPrompts,
                      [current.promptMode]: undefined,
                    },
                  }))
                }
              >
                <RefreshCw size={14} /> 恢复模板
              </button>
            )}
            <button onClick={copyPrompt}>
              <Copy size={14} /> {copyLabel}
            </button>
            <button
              onClick={() =>
                downloadText(
                  `scholarflow_${state.promptMode}_prompt.md`,
                  prompt,
                  "text/markdown;charset=utf-8",
                )
              }
            >
              <Download size={14} /> 下载
            </button>
          </div>
        </div>
        <pre>{prompt}</pre>
        <div className="prompt-actions">
          <AiButton
            task="prompt_refinement"
            busy={busy}
            onClick={refinePrompt}
          >
            让模型去重与消歧
          </AiButton>
          <button className="secondary-button" onClick={saveToWorkspace}>
            <Save size={17} /> 写入工作区
          </button>
        </div>
      </section>

      <section className="panel memory-vault">
        <div className="panel-heading">
          <div>
            <span className="step-badge">B</span>
            <h2>上下文胶囊与审计</h2>
          </div>
          <button className="secondary-button" onClick={compressContext}>
            {busy === "context_compression" ? (
              <LoaderCircle size={16} className="spin" />
            ) : (
              <GitBranch size={16} />
            )}
            压缩当前状态
          </button>
        </div>
        <div className="memory-stats">
          <div>
            <span>项目状态</span>
            <strong>{tokenEstimate.toLocaleString()}</strong>
            <small>估算 tokens</small>
          </div>
          <div>
            <span>上下文预算</span>
            <strong>{state.memory.contextBudget.toLocaleString()}</strong>
            <small>调用前自动收窄</small>
          </div>
          <div>
            <span>稳定胶囊</span>
            <strong>{state.memory.capsules.length}</strong>
            <small>阶段记忆</small>
          </div>
          <div>
            <span>完整交互</span>
            <strong>{logs.length}</strong>
            <small>IndexedDB + 可同步 JSONL</small>
          </div>
        </div>
        {state.memory.capsules.length > 0 && (
          <div className="capsule-list">
            {state.memory.capsules
              .slice()
              .reverse()
              .map((capsule) => (
                <article key={capsule.id}>
                  <div>
                    <span>{capsule.stage}</span>
                    <time>{formatTime(capsule.createdAt)}</time>
                  </div>
                  <h3>{capsule.title}</h3>
                  <p>{capsule.summary}</p>
                  <small>
                    {capsule.decisions.length} 项决定 ·{" "}
                    {capsule.openQuestions.length} 个未决问题
                  </small>
                </article>
              ))}
          </div>
        )}
        <div className="button-row">
          <button
            className="text-button"
            onClick={() =>
              downloadText(
                `scholarflow_${state.projectName}_ai_logs.jsonl`,
                logs.map((log) => JSON.stringify(log)).join("\n"),
                "application/x-ndjson;charset=utf-8",
              )
            }
          >
            <Download size={15} /> 导出完整交互 JSONL
          </button>
          <button
            className="text-button"
            onClick={() =>
              downloadText(
                `scholarflow_${state.projectName}_project.json`,
                JSON.stringify(state, null, 2),
                "application/json;charset=utf-8",
              )
            }
          >
            <Download size={15} /> 导出项目快照
          </button>
        </div>
        {deterministicPrompt !== prompt && (
          <p className="audit-note">
            <ShieldCheck size={15} />
            润色版与确定性模板都保留在项目状态中，可随时回退。
          </p>
        )}
      </section>
    </>
  );
}

function WorkspaceStage({
  state,
  busy,
  logs,
  directoryHandle,
  updateState,
  bindWorkspace,
  scanWorkspace,
  scaffoldWorkspace,
  syncAudit,
  analyzeWorkspace,
}: {
  state: ProjectState;
  busy: string;
  logs: AiLog[];
  directoryHandle: FileSystemDirectoryHandle | null;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  bindWorkspace: () => void;
  scanWorkspace: () => void;
  scaffoldWorkspace: () => void;
  syncAudit: () => void;
  analyzeWorkspace: () => void;
}) {
  const report = state.workspace.report;
  return (
    <>
      <StageIntro
        eyebrow="07 · LOCAL WORKSPACE"
        title="让 Codex 的产物回到可检查的目录"
        description="浏览器只获得你主动选择的目录权限。ScholarFlow 扫描阶段产物、补写 AI 日志，并把缺口翻译成下一条可执行指令。"
        icon={<FolderOpen size={25} />}
      />
      <section className="workspace-hero">
        <div className="workspace-binding">
          <div className="folder-visual">
            <FolderOpen size={30} />
          </div>
          <div>
            <span>当前软绑定</span>
            <h2>{directoryHandle?.name || "尚未选择目录"}</h2>
            <p>
              {directoryHandle
                ? "页面可以在授权后读取和写入该目录；绝对路径仅用于生成 Codex 指令。"
                : "推荐选择一个新的论文项目根目录，或已有的阶段化项目目录。"}
            </p>
          </div>
          <button className="primary-button" onClick={bindWorkspace}>
            <FolderOpen size={17} />
            {directoryHandle ? "重新绑定" : "选择本地目录"}
          </button>
        </div>
        <Field
          label="Codex 使用的绝对路径"
          hint="浏览器不会暴露真实绝对路径，需要从资源管理器复制一次。"
        >
          <input
            value={state.workspace.absolutePath}
            onChange={(event) =>
              updateState((current) => ({
                ...current,
                workspace: {
                  ...current.workspace,
                  absolutePath: event.target.value,
                },
              }))
            }
            placeholder="D:\Research\MyPaper"
          />
        </Field>
        <div className="workspace-actions">
          <button
            className="secondary-button"
            onClick={scaffoldWorkspace}
            disabled={!directoryHandle || Boolean(busy)}
          >
            {busy === "workspace_scaffold" ? (
              <LoaderCircle size={17} className="spin" />
            ) : (
              <LayoutTemplate size={17} />
            )}
            建立标准目录
          </button>
          <button
            className="secondary-button"
            onClick={syncAudit}
            disabled={!directoryHandle || Boolean(busy)}
          >
            {busy === "audit_sync" ? (
              <LoaderCircle size={17} className="spin" />
            ) : (
              <Archive size={17} />
            )}
            补写 {logs.length} 条审计记录
          </button>
          <button
            className="primary-button"
            onClick={scanWorkspace}
            disabled={!directoryHandle || Boolean(busy)}
          >
            {busy === "workspace_scan" ? (
              <LoaderCircle size={17} className="spin" />
            ) : (
              <Search size={17} />
            )}
            扫描阶段产物
          </button>
        </div>
      </section>

      {report ? (
        <>
          <section className="workspace-score-panel">
            <div className="workspace-score-ring">
              <strong>{report.score}</strong>
              <span>证据链完成度</span>
            </div>
            <div>
              <span className="eyebrow">LAST SCAN · {formatTime(report.scannedAt)}</span>
              <h2>{report.fileCount} 个可见文件</h2>
              <p>
                分数只表示标准产物是否可定位，不代表论文质量或实验成功。
                缺失项会被转换成可执行建议。
              </p>
            </div>
            <AiButton
              task="workspace_analysis"
              busy={busy}
              onClick={analyzeWorkspace}
            >
              分析证据链缺口
            </AiButton>
          </section>
          <div className="check-grid">
            {report.checks.map((check) => (
              <article
                key={check.id}
                className={classNames("check-card", `check-${check.status}`)}
              >
                <div className="check-card-head">
                  {check.status === "complete" ? (
                    <CheckCircle2 size={19} />
                  ) : check.status === "partial" ? (
                    <Activity size={19} />
                  ) : (
                    <Circle size={19} />
                  )}
                  <strong>{check.label}</strong>
                  <span>
                    {check.status === "complete"
                      ? "完整"
                      : check.status === "partial"
                        ? "部分"
                        : "缺失"}
                  </span>
                </div>
                <p>{check.detail}</p>
                {check.matchedFiles.length > 0 && (
                  <code>{check.matchedFiles[0]}</code>
                )}
              </article>
            ))}
          </div>
          {report.aiAnalysis && (
            <section className="panel workspace-analysis">
              <div className="panel-heading">
                <div>
                  <span className="step-badge">
                    <Gauge size={14} />
                  </span>
                  <h2>模型工作区诊断</h2>
                </div>
                <span className="topic-chip">
                  证据健康度 {report.aiAnalysis.score}
                </span>
              </div>
              <p className="lead-copy">{report.aiAnalysis.summary}</p>
              <div className="generated-grid">
                <div>
                  <strong>下一步动作</strong>
                  <ol>
                    {report.aiAnalysis.nextActions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                </div>
                <div>
                  <strong>学术习惯提醒</strong>
                  <ul>
                    {report.aiAnalysis.habitWarnings.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          )}
        </>
      ) : (
        <EmptyState
          icon={<Database size={27} />}
          title="尚未扫描项目"
          description="绑定后可先建立标准目录，再让 Codex 按固定位置写入。已有项目也可以直接扫描，不会修改文件。"
        />
      )}
    </>
  );
}

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="empty-state">
      <div>{icon}</div>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

function PromptPreviewModal({
  state,
  prompt,
  updateState,
  onClose,
}: {
  state: ProjectState;
  prompt: string;
  updateState: (updater: (current: ProjectState) => ProjectState) => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="settings-modal wide-modal prompt-preview-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-preview-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">LIVE PROMPT</span>
            <h2 id="prompt-preview-title">最新可执行提示词</h2>
            <p>
              每个表单字段都会即时进入确定性模板，不必等到第 6 阶段才看见结果。
            </p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </div>
        <div className="prompt-mode-grid compact-mode-grid">
          {PROMPT_MODES.map((mode) => {
            const modePrompt = buildMasterPrompt(state, mode.id);
            return (
              <button
                key={mode.id}
                className={classNames(
                  "prompt-mode-card",
                  state.promptMode === mode.id && "selected",
                )}
                onClick={() =>
                  updateState((current) => ({
                    ...current,
                    promptMode: mode.id,
                  }))
                }
              >
                <strong>{mode.label}</strong>
                <small>{mode.description}</small>
                <span className="mode-context-line">
                  <b>上下文</b>
                  {mode.context}
                </span>
                <span className="mode-context-line">
                  <b>产出</b>
                  {mode.deliverable}
                </span>
                <span className="mode-context-line mode-exclusion-line">
                  <b>排除</b>
                  {mode.excludes}
                </span>
                <em>{estimatePromptTokens(modePrompt).toLocaleString()} tokens</em>
              </button>
            );
          })}
        </div>
        <div className="preview-skill-line">
          <GraduationCap size={17} />
          <span>
            已嵌入 {state.selectedSkills.length} 个专业技能路由；缺失技能会要求
            Codex 先检查或安装。
          </span>
        </div>
        <div className="prompt-editor global-prompt-editor">
          <div className="prompt-toolbar">
            <span>实时确定性模板</span>
            <div className="prompt-actions">
              <button
                className="toolbar-button"
                onClick={() => void navigator.clipboard.writeText(prompt)}
              >
                <Copy size={14} /> 复制
              </button>
              <button
                className="toolbar-button"
                onClick={() =>
                  downloadText(
                    `scholarflow-${state.promptMode}-prompt.md`,
                    prompt,
                  )
                }
              >
                <Download size={14} /> 下载
              </button>
            </div>
          </div>
          <pre>{prompt}</pre>
        </div>
      </section>
    </div>
  );
}

function RequirementIntakeModal({
  draft,
  onlyBlank,
  result,
  busy,
  setDraft,
  setOnlyBlank,
  onAnalyze,
  onApply,
  onClose,
}: {
  draft: string;
  onlyBlank: boolean;
  result: RequirementIntakeResult | null;
  busy: string;
  setDraft: (value: string) => void;
  setOnlyBlank: (value: boolean) => void;
  onAnalyze: () => void;
  onApply: () => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="settings-modal wide-modal requirement-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="requirement-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">REQUIREMENT INBOX</span>
            <h2 id="requirement-title">把零散要求放进正确表单</h2>
            <p>
              可以粘贴导师意见、个人偏好、实验约束或新增设想。已连接模型
              先给出字段映射，你确认后才写入。
            </p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </div>
        <Field
          label="新增要求"
          hint="解析内容会进入本地审计日志；API key 不会进入请求正文。"
        >
          <textarea
            rows={7}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="例如：我只有一张 16GB GPU；主实验要比较四类框架；必须报告成本、鲁棒性和失败案例；图要用可编辑 draw.io……"
          />
        </Field>
        <label className="merge-policy">
          <input
            type="checkbox"
            checked={onlyBlank}
            onChange={(event) => setOnlyBlank(event.target.checked)}
          />
          <span>
            <strong>只填空白字段</strong>
            <small>推荐。已有决定不被新输入静默覆盖。</small>
          </span>
        </label>
        <div className="button-row">
          <AiButton
            task="requirement_intake"
            busy={busy}
            onClick={onAnalyze}
          >
            让模型解析字段
          </AiButton>
        </div>

        {result && (
          <div className="requirement-result">
            <div className="requirement-summary">
              <div>
                <span className="eyebrow">PARSE PREVIEW</span>
                <strong>{result.summary}</strong>
              </div>
              <span className={`confidence-tag ${result.confidence}`}>
                {result.confidence} confidence
              </span>
            </div>
            <div className="detected-stages">
              {result.detectedStages.map((stage) => (
                <span key={stage}>{STAGES.find((item) => item.id === stage)?.label || stage}</span>
              ))}
            </div>
            <div className="mapping-list">
              {result.mappings.map((mapping, index) => (
                <article key={`${mapping.targetField}-${index}`}>
                  <span className="mapping-source">“{mapping.sourceQuote}”</span>
                  <ChevronRight size={14} />
                  <div>
                    <strong>{mapping.targetField}</strong>
                    <p>{mapping.value}</p>
                  </div>
                </article>
              ))}
            </div>
            {result.warnings.length > 0 && (
              <div className="parse-warnings">
                <Info size={16} />
                <ul>
                  {result.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="modal-footer">
              <span className="status-line">
                <ShieldCheck size={13} />
                {onlyBlank ? "已有字段保持不变" : "允许更新已有字段"}
              </span>
              <button className="primary-button" onClick={onApply}>
                <Check size={16} /> 确认写入对应表单
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function SkillsModal({
  selected,
  onToggle,
  onDownload,
  onClose,
}: {
  selected: string[];
  onToggle: (skillId: string) => void;
  onDownload: () => void;
  onClose: () => void;
}) {
  const categories = ["调研", "写作", "图表", "工程", "评审"] as const;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="settings-modal wide-modal skills-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="skills-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">ACADEMIC SKILLS ROUTER</span>
            <h2 id="skills-title">为任务选择专业技能</h2>
            <p>
              勾选项会直接写入最新提示词。ScholarFlow 自带技能可下载，外部技能
              只记录名称和安装要求，不复制第三方源码。
            </p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </div>
        <div className="skills-toolbar">
          <div>
            <strong>{selected.length}</strong>
            <span>个技能已进入提示词</span>
          </div>
          <a
            className="primary-button"
            href="scholarflow-academic-skills-pack.zip"
            download
            onClick={onDownload}
          >
            <Download size={16} /> 下载 ScholarFlow Skills Pack
          </a>
        </div>
        <div className="skills-catalog">
          {categories.map((category) => (
            <section key={category}>
              <h3>{category}</h3>
              <div className="skill-card-grid">
                {SKILL_CATALOG.filter(
                  (skill) => skill.category === category,
                ).map((skill) => {
                  const enabled = selected.includes(skill.id);
                  return (
                    <button
                      className={classNames(
                        "skill-select-card",
                        enabled && "selected",
                      )}
                      key={skill.id}
                      onClick={() => onToggle(skill.id)}
                    >
                      <span className="skill-select-head">
                        <code>${skill.name}</code>
                        {skill.bundled && <em>PACK</em>}
                        {enabled && <CheckCircle2 size={16} />}
                      </span>
                      <strong>{skill.description}</strong>
                      <small>{skill.useWhen}</small>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        <div className="privacy-banner skills-install-note">
          <Info size={19} />
          <p>
            提示词会要求 Codex：先检查技能是否存在，完整阅读 SKILL.md；
            缺失时调用 <code>$skill-installer</code> 搜索同名技能。安装失败则明确
            报告并使用最接近的可审计流程。
          </p>
        </div>
      </section>
    </div>
  );
}

function SettingsModal({
  config,
  proxyStatus,
  setConfig,
  uiScale,
  setUiScale,
  busy,
  onTest,
  onClear,
  onSave,
  onClose,
}: {
  config: AiConfig;
  proxyStatus: { checked: boolean; configured: boolean; model: string };
  setConfig: React.Dispatch<React.SetStateAction<AiConfig>>;
  uiScale: UiScale;
  setUiScale: (scale: UiScale) => void;
  busy: string;
  onTest: () => void;
  onClear: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const staticHost = isStaticPagesHost();
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="settings-modal connection-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">MODEL CONNECTION</span>
            <h2 id="settings-title">模型连接、显示与本地隐私</h2>
            <p>选择供应商或填写任意 OpenAI-compatible 端点。</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={19} />
          </button>
        </div>
        <div className="privacy-banner">
          <ShieldCheck size={20} />
          <p>
            API key 默认只保留当前会话。勾选“记住本浏览器”后才会写入浏览器
            localStorage；它始终不会进入项目包、AI 日志或工作区文件。
          </p>
        </div>
        <Field label="模型供应商">
          <div className="provider-grid">
            {PROVIDER_PRESETS.map((provider) => (
              <button
                key={provider.id}
                className={classNames(
                  "provider-option",
                  config.provider === provider.id && "selected",
                )}
                onClick={() =>
                  setConfig((current) => ({
                    ...current,
                    provider: provider.id,
                    mode: staticHost ? "direct" : current.mode,
                    baseUrl: provider.baseUrl,
                    model: provider.model,
                    apiKey:
                      provider.id === "ollama" ? "" : current.apiKey,
                  }))
                }
              >
                <strong>{provider.label}</strong>
                <small>{provider.hint}</small>
              </button>
            ))}
          </div>
        </Field>
        <Field label="连接方式">
          <div className="connection-options">
            <button
              className={classNames(
                "connection-option",
                config.mode === "proxy" && "selected",
              )}
              disabled={staticHost}
              onClick={() =>
                setConfig((current) => ({ ...current, mode: "proxy" }))
              }
            >
              <Database size={19} />
              <span>
                <strong>同源无状态代理</strong>
                <small>
                  {staticHost
                    ? "GitHub Pages 无后端，请使用浏览器直连。"
                    : "由自托管环境变量控制供应商和 key。"}
                </small>
              </span>
              {config.mode === "proxy" && <CheckCircle2 size={17} />}
            </button>
            <button
              className={classNames(
                "connection-option",
                config.mode === "direct" && "selected",
              )}
              onClick={() =>
                setConfig((current) => ({ ...current, mode: "direct" }))
              }
            >
              <ExternalLink size={19} />
              <span>
                <strong>浏览器直连</strong>
                <small>无需部署；供应商必须允许浏览器 CORS。</small>
              </span>
              {config.mode === "direct" && <CheckCircle2 size={17} />}
            </button>
          </div>
        </Field>
        <div className="form-grid">
          <Field
            label="模型 ID"
            hint="保持自由输入，避免模型目录更新后被固定选项限制。"
          >
            <input
              value={config.model}
              onChange={(event) =>
                setConfig((current) => ({
                  ...current,
                  model: event.target.value,
                }))
              }
              placeholder="例如：供应商控制台中的模型 ID"
            />
          </Field>
          <Field label="Base URL">
            <input
              value={config.baseUrl}
              onChange={(event) =>
                setConfig((current) => ({
                  ...current,
                  baseUrl: event.target.value,
                }))
              }
              disabled={config.mode === "proxy"}
            />
          </Field>
          <Field
            label={
              config.provider === "ollama"
                ? "API key（本地 Ollama 可留空）"
                : "API key"
            }
            hint={
              proxyStatus.configured
                ? "代理已配置环境变量，此处可以留空。"
                : config.persistKey
                  ? "已选择记住在本浏览器；共享设备不建议开启。"
                  : "默认关闭标签页后清除。"
            }
            className="span-2"
          >
            <input
              type="password"
              autoComplete="off"
              value={config.apiKey}
              onChange={(event) =>
                setConfig((current) => ({
                  ...current,
                  apiKey: event.target.value,
                }))
              }
              placeholder={
                proxyStatus.configured
                  ? "使用部署环境中的 key"
                  : "sk-…"
              }
            />
          </Field>
        </div>
        <div className="settings-toggles">
          <label className="merge-policy">
            <input
              type="checkbox"
              checked={config.persistKey}
              onChange={(event) =>
                setConfig((current) => ({
                  ...current,
                  persistKey: event.target.checked,
                }))
              }
            />
            <span>
              <strong>记住在本浏览器</strong>
              <small>仅在个人设备开启；可用下方按钮一键清空。</small>
            </span>
          </label>
          <label className="merge-policy">
            <input
              type="checkbox"
              checked={config.jsonMode}
              onChange={(event) =>
                setConfig((current) => ({
                  ...current,
                  jsonMode: event.target.checked,
                }))
              }
            />
            <span>
              <strong>请求 JSON mode</strong>
              <small>若自定义服务不支持 response_format，可关闭。</small>
            </span>
          </label>
        </div>
        <Field label="界面字号">
          <div className="font-scale-options">
            {([
              ["compact", "紧凑", "适合小屏"],
              ["standard", "标准", "默认更清晰"],
              ["large", "大号", "远距离阅读"],
            ] as const).map(([id, label, hint]) => (
              <button
                key={id}
                className={classNames(
                  "font-scale-option",
                  uiScale === id && "selected",
                )}
                onClick={() => setUiScale(id)}
              >
                <Type size={id === "large" ? 21 : id === "standard" ? 18 : 15} />
                <span><strong>{label}</strong><small>{hint}</small></span>
              </button>
            ))}
          </div>
        </Field>
        <div className="modal-footer">
          <span className="status-line">
            <Circle
              size={10}
              fill={proxyStatus.configured ? "currentColor" : "none"}
            />
            {proxyStatus.configured
              ? `代理环境已配置 · ${proxyStatus.model}`
              : config.mode === "direct"
                ? `${providerLabel(config.provider)} · 浏览器直连`
                : "代理未配置环境 key，可使用浏览器 BYOK"}
          </span>
          <div className="button-row">
            <button
              className="secondary-button"
              onClick={onTest}
              disabled={Boolean(busy)}
            >
              {busy === "requirement_intake" ? (
                <LoaderCircle size={16} className="spin" />
              ) : (
                <Sparkles size={16} />
              )}
              测试连接
            </button>
            <button className="primary-button" onClick={onSave}>
              <Save size={17} /> 保存连接
            </button>
          </div>
        </div>
        <div className="clear-data-zone">
          <div>
            <Trash2 size={18} />
            <span>
              <strong>离开前清空本机痕迹</strong>
              <small>
                删除表单、日志、目录句柄、连接、缓存 key 和显示偏好。
              </small>
            </span>
          </div>
          <button className="danger-button" onClick={onClear}>
            <Trash2 size={15} /> 一键清空
          </button>
        </div>
      </section>
    </div>
  );
}
